import React, { useState, useEffect } from 'react';
import { 
  Page, 
  Layout,
  Card,
  Button,
  ButtonGroup,
  Modal,
  FormLayout,
  TextField,
  Select,
  Badge,
  Banner,
  Tabs,
  DataTable,
  Checkbox,
  Pagination,
  Filters,
  ResourceList,
  ResourceItem,
  TextStyle,
  Stack,
  Icon,
  TextContainer,
  EmptyState,
  Tooltip,
  Link,
  Spinner,
  Text
} from '@shopify/polaris';
import { SearchMinor, DuplicateMinor } from '@shopify/polaris-icons';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import {
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  writeBatch, 
  setDoc,
  query,
  where,
  orderBy,
  limit,
  addDoc
} from 'firebase/firestore';
import SystemSettingsPage from './admin/SystemSettingsPage';
import ServerStatusPage from './admin/ServerStatusPage';
import AIModelManagement from './admin/AIModelManagement';
import BetaManagementPage from './admin/BetaManagementPage';
import { Navigate } from 'react-router-dom';
import {
  getSubscriptionPlans, 
  updateSubscriptionPlan, 
  setUserSubscription, 
  addFlexCredits,
  initializeSubscriptionPlans,
  cancelUserSubscription,
  SUBSCRIPTION_TYPES,
  migrateOldSubscriptionTypes,
  getFlexCreditPackages,
  updateFlexCreditPackage,
  initializeFlexCreditPackages,
  purchaseFlexCreditPackage,
  FLEX_CREDIT_TYPES,
  setUserVIPStatus,
  removeUserVIPStatus
} from '../services/subscriptionService';
import {
  getStripeSettings,
  updateStripeSettings,
  initializeStripeSettings
} from '../services/stripeService';
import { SystemSettingsService } from '../services/systemSettingsService';
import SummernoteEditor from '../components/SummernoteEditor';

export default function AdminPanel() {
  const { user, isAdmin } = useAuth();
  // User management state
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [editUser, setEditUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editCredits, setEditCredits] = useState('');
  const [addingFlexCreditsToAll, setAddingFlexCreditsToAll] = useState(false);
  const [flexCreditsToAdd, setFlexCreditsToAdd] = useState('');
  const [logs, setLogs] = useState([]);
  const [logFilters, setLogFilters] = useState({
    level: '',
    dateFrom: '',
    dateTo: '',
    search: ''
  });
  const [userFilters, setUserFilters] = useState({
    isAdmin: false,
    isActive: false,
    hasSubscription: false,
    search: ''
  });
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  // New state for subscription management
  const [subscriptionPlans, setSubscriptionPlans] = useState({});
  const [editPlanDialogOpen, setEditPlanDialogOpen] = useState(false);
  const [editPlanType, setEditPlanType] = useState('');
  const [editPlanName, setEditPlanName] = useState('');
  const [editPlanCredits, setEditPlanCredits] = useState('');
  const [editPlanPrice, setEditPlanPrice] = useState('');
  const [editPlanEnabled, setEditPlanEnabled] = useState(true);
  const [editPlanStripeProductId, setEditPlanStripeProductId] = useState('');
  const [editPlanStripePriceId, setEditPlanStripePriceId] = useState('');
  const [editPlanYearlyProductId, setEditPlanYearlyProductId] = useState('');
  const [editPlanYearlyPriceId, setEditPlanYearlyPriceId] = useState('');
  const [editPlanYearlyDiscountPercentage, setEditPlanYearlyDiscountPercentage] = useState('');
  const [editPlanFeatures, setEditPlanFeatures] = useState([]);
  const [editPlanMaxProductSelection, setEditPlanMaxProductSelection] = useState('');
  const [editPlanDescription, setEditPlanDescription] = useState('');
  const [editPlanProductCount, setEditPlanProductCount] = useState('');
  const [editPlanProductCountExplanation, setEditPlanProductCountExplanation] = useState('');
  const [assignSubscriptionDialogOpen, setAssignSubscriptionDialogOpen] = useState(false);
  const [selectedSubscriptionType, setSelectedSubscriptionType] = useState(SUBSCRIPTION_TYPES.PLAN_1);
  const [addFlexCreditsDialogOpen, setAddFlexCreditsDialogOpen] = useState(false);
  
  // New state for flex credit packages management
  const [flexCreditPackages, setFlexCreditPackages] = useState({});
  const [editPackageDialogOpen, setEditPackageDialogOpen] = useState(false);
  const [editPackageType, setEditPackageType] = useState('');
  const [editPackageName, setEditPackageName] = useState('');
  const [editPackageCredits, setEditPackageCredits] = useState('');
  const [editPackagePrice, setEditPackagePrice] = useState('');
  const [editPackageEnabled, setEditPackageEnabled] = useState(true);
  const [editPackageStripeProductId, setEditPackageStripeProductId] = useState('');
  const [editPackageStripePriceId, setEditPackageStripePriceId] = useState('');
  const [editPackageDescription, setEditPackageDescription] = useState('');
  const [editPackageCreditCountText, setEditPackageCreditCountText] = useState('');
  const [editPackageCreditCountExplanation, setEditPackageCreditCountExplanation] = useState('');
  const [purchasePackageDialogOpen, setPurchasePackageDialogOpen] = useState(false);
  const [selectedPackageType, setSelectedPackageType] = useState(FLEX_CREDIT_TYPES.FLEX_1);
  
  // State for Stripe settings
  const [stripeSettings, setStripeSettings] = useState({});
  const [editStripeSettingsDialogOpen, setEditStripeSettingsDialogOpen] = useState(false);
  const [editStripeApiKey, setEditStripeApiKey] = useState('');
  const [editStripePublishableKey, setEditStripePublishableKey] = useState('');
  const [editStripeWebhookSecret, setEditStripeWebhookSecret] = useState('');
  const [editStripeTestMode, setEditStripeTestMode] = useState(true);

  // State for editing plan features
  const [newFeature, setNewFeature] = useState('');

  // State for VIP status dialog
  const [vipDialogOpen, setVipDialogOpen] = useState(false);
  const [vipUserId, setVipUserId] = useState('');
  const [vipPlanType, setVipPlanType] = useState(SUBSCRIPTION_TYPES.PLAN_3);
  const [vipDuration, setVipDuration] = useState('unlimited');
  const [vipMonths, setVipMonths] = useState(3);
  const [vipDurationType, setVipDurationType] = useState('unlimited');

  // State for sales management
  const [saleName, setSaleName] = useState('');
  const [subscriptionSalePercentage, setSubscriptionSalePercentage] = useState(0);
  const [creditsSalePercentage, setCreditsSalePercentage] = useState(0);
  const [salesDialogOpen, setSalesDialogOpen] = useState(false);
  const [activeSales, setActiveSales] = useState({
    subscription: { active: false, name: '', percentage: 0 },
    credits: { active: false, name: '', percentage: 0 }
  });

  // State for user management pagination and search
  const [searchValue, setSearchValue] = useState('');
  const [appliedFilters, setAppliedFilters] = useState([]);

  // State for credit management search and pagination
  const [searchCreditValue, setSearchCreditValue] = useState('');
  const [creditCurrentPage, setCreditCurrentPage] = useState(1);
  const creditsPerPage = 20;

  // Maintenance mode state
  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
  const [maintenanceTitle, setMaintenanceTitle] = useState('System Maintenance');
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [maintenanceStartDate, setMaintenanceStartDate] = useState('');
  const [maintenanceStartTime, setMaintenanceStartTime] = useState('');
  const [maintenanceEndDate, setMaintenanceEndDate] = useState('');
  const [maintenanceEndTime, setMaintenanceEndTime] = useState('');
  const [maintenanceInfo, setMaintenanceInfo] = useState(null);
  const [maintenanceLoading, setMaintenanceLoading] = useState(true);

  // Referral system state
  const [referralPartners, setReferralPartners] = useState([]);
  const [referralStats, setReferralStats] = useState({});
  const [createReferralDialogOpen, setCreateReferralDialogOpen] = useState(false);
  const [editReferralDialogOpen, setEditReferralDialogOpen] = useState(false);
  const [editReferralPartner, setEditReferralPartner] = useState(null);
  const [newReferralName, setNewReferralName] = useState('');
  const [newReferralEmail, setNewReferralEmail] = useState('');
  const [newReferralCommission, setNewReferralCommission] = useState('10');
  const [referralSearchValue, setReferralSearchValue] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [filteredReferrals, setFilteredReferrals] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [referralCurrentPage, setReferralCurrentPage] = useState(1);
  const referralsPerPage = 10;

  // State for referral code input
  const [manualReferralCode, setManualReferralCode] = useState('');
  const [useManualCode, setUseManualCode] = useState(false);

  // State for referral payouts
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [selectedPartnerForPayout, setSelectedPartnerForPayout] = useState(null);
  const [editingPayout, setEditingPayout] = useState(null);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutTransactionId, setPayoutTransactionId] = useState('');
  const [payoutStatus, setPayoutStatus] = useState('completed');
  const [pendingPayouts, setPendingPayouts] = useState({});
  const [minSubscriptionDays, setMinSubscriptionDays] = useState(30); // Default to 30 days

  // State for payout history
  const [payoutHistory, setPayoutHistory] = useState([]);

  // State for referral bonus settings
  const [referralBonusSettings, setReferralBonusSettings] = useState(500);
  const [showReferralBonusModal, setShowReferralBonusModal] = useState(false);

  // Trial plan state
  const [trialPlanSettings, setTrialPlanSettings] = useState({
    credits: 20,
    duration_days: 1,
    stripe_product_id: '',
    stripe_price_id: ''
  });
  const [showTrialPlanModal, setShowTrialPlanModal] = useState(false);
  const [loadingTrialPlan, setLoadingTrialPlan] = useState(false);

  // State for status messages
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchSubscriptionPlans();
    fetchFlexCreditPackages();
    fetchStripeSettings();
    fetchActiveSales();
    fetchMaintenanceInfo();
    fetchReferralPartners();
    fetchReferralSettings(); // Fetch referral settings
    // Run migration when the admin panel loads
    migrateSubscriptionTypes();
  }, []);

  useEffect(() => {
    if (selectedTab === 7) { 
      fetchLogs();
    }
  }, [selectedTab, logFilters]);

  useEffect(() => {
    // Filter users based on search value
    filterUsers();
  }, [users, searchValue, appliedFilters]);

  useEffect(() => {
    filterReferralPartners();
  }, [referralSearchValue, referralPartners]);

  const fetchUsers = async () => {
    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      const usersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setLoading(false);
    }
  };

  const fetchSubscriptionPlans = async () => {
    try {
      // Fetch the plans directly from Firestore first
      const plansRef = collection(db, 'subscriptionPlans');
      const snapshot = await getDocs(plansRef);
      
      // Only initialize if no plans exist
      if (snapshot.empty) {
        console.log('No subscription plans found, initializing defaults...');
        await initializeSubscriptionPlans();
        // Fetch again after initialization
        const newSnapshot = await getDocs(plansRef);
        const plans = {};
        newSnapshot.docs.forEach(doc => {
          plans[doc.id] = doc.data();
        });
        console.log('Fetched initialized subscription plans:', plans);
        setSubscriptionPlans(plans);
      } else {
        // Plans exist, use them
        const plans = {};
        snapshot.docs.forEach(doc => {
          plans[doc.id] = doc.data();
        });
        console.log('Fetched existing subscription plans:', plans);
        setSubscriptionPlans(plans);
      }
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
    }
  };

  const fetchFlexCreditPackages = async () => {
    try {
      // Fetch the packages directly from Firestore first
      const packagesRef = collection(db, 'flexCreditPackages');
      const snapshot = await getDocs(packagesRef);
      
      // Only initialize if no packages exist
      if (snapshot.empty) {
        console.log('No flex credit packages found, initializing defaults...');
        await initializeFlexCreditPackages();
        // Fetch again after initialization
        const newSnapshot = await getDocs(packagesRef);
        const packages = {};
        newSnapshot.docs.forEach(doc => {
          packages[doc.id] = doc.data();
        });
        console.log('Fetched initialized flex credit packages:', packages);
        setFlexCreditPackages(packages);
      } else {
        // Packages exist, use them
        const packages = {};
        snapshot.docs.forEach(doc => {
          packages[doc.id] = doc.data();
        });
        console.log('Fetched existing flex credit packages:', packages);
        setFlexCreditPackages(packages);
      }
    } catch (error) {
      console.error('Error fetching flex credit packages:', error);
    }
  };

  const fetchStripeSettings = async () => {
    try {
      // First explicitly initialize Stripe settings
      await initializeStripeSettings();
      // Then fetch the settings
      const settings = await getStripeSettings();
      setStripeSettings(settings);
    } catch (error) {
      console.error('Error fetching Stripe settings:', error);
    }
  };

  const fetchActiveSales = async () => {
    try {
      // Get sales document from Firestore
      const salesRef = doc(db, 'settings', 'sales');
      const salesDoc = await getDoc(salesRef);
      
      if (salesDoc.exists()) {
        const salesData = salesDoc.data();
        setActiveSales({
          subscription: salesData.subscription || { active: false, name: '', percentage: 0 },
          credits: salesData.credits || { active: false, name: '', percentage: 0 }
        });
        
        // Update the form fields with active sale data
        if (salesData.subscription?.active) {
          setSaleName(salesData.subscription.name);
          setSubscriptionSalePercentage(salesData.subscription.percentage);
        }
        
        if (salesData.credits?.active) {
          // Only update name if subscription sale name is not already set
          if (!salesData.subscription?.active) {
            setSaleName(salesData.credits.name);
          }
          setCreditsSalePercentage(salesData.credits.percentage);
        }
      }
    } catch (error) {
      console.error('Error fetching active sales:', error);
    }
  };

  const handleEditUser = (user) => {
    setEditUser(user);
    setEditCredits(user.credits?.total || '0');
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const userRef = doc(db, 'users', editUser.id);
      await updateDoc(userRef, {
        'credits.total': parseInt(editCredits),
        'credits.monthly': parseInt(editCredits) // Also update monthly credits for compatibility
      });
      setEditDialogOpen(false);
      await fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  // Delete a user and all associated data
  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This will permanently remove their account, data, and authentication.')) {
      try {
        setLoading(true);
        
        // Get user data before deletion for referral cleanup
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();
        
        // 1. Delete user document from Firestore
        await deleteDoc(userRef);
        
        // 2. Check if user is a referral partner and delete that entry
        if (userData) {
          // Find and delete referral partner entry if exists
          const partnersRef = collection(db, 'referralSystem');
          const q = query(partnersRef, where('partnerId', '==', userId));
          const partnerSnapshot = await getDocs(q);
          
          if (!partnerSnapshot.empty) {
            const partnerId = partnerSnapshot.docs[0].id;
            await deleteDoc(doc(db, 'referralSystem', partnerId));
            console.log('Deleted referral partner entry for user:', userId);
          }
        }
        
        // 3. Delete Firebase Authentication account
        // This requires admin SDK, so we'll use a Cloud Function
        // For now, show a message to manually delete the auth account
        alert('User document and referral data deleted. Note: You must also delete the user from Firebase Authentication console.');
        
        // 4. Refresh the user list
        await fetchUsers();
        setSuccess('User successfully deleted');
      } catch (error) {
        console.error('Error deleting user:', error);
        setError('Failed to delete user: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleMakeAdmin = async (userId) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { isAdmin: true });
      await fetchUsers();
    } catch (error) {
      console.error('Error making user admin:', error);
    }
  };

  const handleRemoveAdmin = async (userId) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { isAdmin: false });
      await fetchUsers();
    } catch (error) {
      console.error('Error removing admin:', error);
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { isActive: true });
      await fetchUsers();
    } catch (error) {
      console.error('Error activating user:', error);
    }
  };

  const handleDeactivateUser = async (userId) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { isActive: false });
      await fetchUsers();
    } catch (error) {
      console.error('Error deactivating user:', error);
    }
  };

  const handleAddFlexCreditsToAll = async () => {
    try {
      console.log('Adding flex credits to all users:', flexCreditsToAdd);
      console.log('Number of users:', users.length);
      
      if (!flexCreditsToAdd || isNaN(parseInt(flexCreditsToAdd)) || parseInt(flexCreditsToAdd) <= 0) {
        console.error('Invalid flex credits value:', flexCreditsToAdd);
        return;
      }
      
      const batch = writeBatch(db);
      
      users.forEach(user => {
        console.log('Processing user:', user.id);
        const userRef = doc(db, 'users', user.id);
        
        // Check if user has credits structure
        if (!user.credits) {
          console.log('User has no credits structure, creating one:', user.id);
          batch.update(userRef, {
            credits: {
              total: 0,
              used: 0,
              monthly: 0,
              flex: parseInt(flexCreditsToAdd),
              lastRefillDate: new Date().toISOString()
            }
          });
        } else {
          // User has credits structure, update flex credits
          const currentFlexCredits = user.credits?.flex || 0;
          console.log('Current flex credits:', currentFlexCredits);
          const newFlexCredits = currentFlexCredits + parseInt(flexCreditsToAdd);
          console.log('New flex credits:', newFlexCredits);
          
          batch.update(userRef, {
            'credits.flex': newFlexCredits
          });
        }
      });
      
      console.log('Committing batch update...');
      await batch.commit();
      console.log('Batch update completed successfully');
      
      setAddingFlexCreditsToAll(false);
      setFlexCreditsToAdd('');
      await fetchUsers();
    } catch (error) {
      console.error('Error adding flex credits to all users:', error);
    }
  };

  // Handle editing a subscription plan
  const handleEditPlan = (planType) => {
    const plan = subscriptionPlans[planType];
    setEditPlanType(planType);
    setEditPlanName(plan.name);
    setEditPlanCredits(plan.monthlyCredits.toString());
    setEditPlanPrice(plan.price.toString());
    setEditPlanEnabled(plan.enabled !== false);
    setEditPlanStripeProductId(plan.stripeProductId || '');
    setEditPlanStripePriceId(plan.stripePriceId || '');
    setEditPlanYearlyProductId(plan.yearlyProductId || '');
    setEditPlanYearlyPriceId(plan.yearlyPriceId || '');
    setEditPlanYearlyDiscountPercentage(plan.yearlyDiscountPercentage || '20');
    setEditPlanFeatures(plan.features || ['Basic features']);
    setEditPlanMaxProductSelection(plan.maxProductSelection || '');
    setEditPlanDescription(plan.description || '');
    setEditPlanProductCount(plan.productCountText || '~5 products');
    setEditPlanProductCountExplanation(plan.productCountExplanation || 'ud83eudde0 Assumption: Each full product edit (title, description, specs, options, benefits, images) costs about 20 credits');
    setEditPlanDialogOpen(true);
  };

  // Save subscription plan changes
  const handleSavePlan = async () => {
    try {
      // Debug admin status
      console.log('Current user admin status:', isAdmin);
      
      if (!isAdmin) {
        alert('You must be an admin to save subscription plans');
        return;
      }
      
      // Get reference to the plan document
      const planRef = doc(db, 'subscriptionPlans', editPlanType);
      
      // Create the updated plan object with all required fields
      const updatedPlan = {
        name: editPlanName,
        monthlyCredits: parseInt(editPlanCredits),
        price: parseFloat(editPlanPrice),
        enabled: editPlanEnabled,
        stripeProductId: editPlanStripeProductId || '',
        stripePriceId: editPlanStripePriceId || '',
        yearlyProductId: editPlanYearlyProductId || '',
        yearlyPriceId: editPlanYearlyPriceId || '',
        yearlyDiscountPercentage: editPlanYearlyDiscountPercentage || '20',
        type: editPlanType,
        features: editPlanFeatures,
        maxProductSelection: parseInt(editPlanMaxProductSelection),
        description: editPlanDescription || '',
        productCountText: editPlanProductCount || '',
        productCountExplanation: editPlanProductCountExplanation || '',
        sortOrder: parseInt(editPlanType.split('_')[1]) || 1,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      console.log('Saving plan to Firestore:', updatedPlan);
      
      try {
        // Use updateSubscriptionPlan from the service instead of direct Firestore access
        await updateSubscriptionPlan(editPlanType, updatedPlan);
        console.log('Plan saved successfully through service');
      } catch (saveError) {
        console.error('Error during save operation:', saveError);
        alert(`Error during save operation: ${saveError.message}`);
        return;
      }
      
      // Close dialog
      setEditPlanDialogOpen(false);
      
      // Refresh the subscription plans
      await fetchSubscriptionPlans();
    } catch (error) {
      console.error('Error updating subscription plan:', error);
      alert('Error saving plan: ' + error.message);
    }
  };

  // Open assign subscription dialog
  const handleAssignSubscription = (user) => {
    setEditUser(user);
    setSelectedSubscriptionType(user.subscription?.type || SUBSCRIPTION_TYPES.PLAN_1);
    setAssignSubscriptionDialogOpen(true);
  };

  // Save subscription assignment
  const handleSaveSubscription = async () => {
    try {
      await setUserSubscription(editUser.id, selectedSubscriptionType, true);
      setAssignSubscriptionDialogOpen(false);
      await fetchUsers();
    } catch (error) {
      console.error('Error assigning subscription:', error);
    }
  };

  // Handle removing a user's subscription
  const handleRemoveSubscription = async (user) => {
    if (window.confirm(`Are you sure you want to remove the subscription for ${user.email}?`)) {
      try {
        await cancelUserSubscription(user.id);
        await fetchUsers();
      } catch (error) {
        console.error('Error removing subscription:', error);
      }
    }
  };

  // Open add flex credits dialog
  const handleAddFlexCredits = (user) => {
    setEditUser(user);
    setFlexCreditsToAdd(user.credits?.flex?.toString() || '0');
    setAddFlexCreditsDialogOpen(true);
  };

  // Save flex credits
  const handleSaveFlexCredits = async () => {
    try {
      // Get the current user document to get the current flex credits
      const userRef = doc(db, 'users', editUser.id);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      
      // Update the flex credits directly to the new value instead of adding
      await updateDoc(userRef, {
        'credits.flex': parseInt(flexCreditsToAdd) || 0
      });
      
      setAddFlexCreditsDialogOpen(false);
      await fetchUsers();
    } catch (error) {
      console.error('Error updating flex credits:', error);
    }
  };

  // Handle editing a flex credit package
  const handleEditPackage = (packageType) => {
    const packageData = flexCreditPackages[packageType];
    setEditPackageType(packageType);
    setEditPackageName(packageData.name);
    setEditPackageCredits(packageData.credits.toString());
    setEditPackagePrice(packageData.price.toString());
    setEditPackageEnabled(packageData.enabled !== false);
    setEditPackageStripeProductId(packageData.stripeProductId);
    setEditPackageStripePriceId(packageData.stripePriceId);
    setEditPackageDescription(packageData.description || '');
    setEditPackageCreditCountText(packageData.creditCountText || '');
    setEditPackageCreditCountExplanation(packageData.creditCountExplanation || '');
    setEditPackageDialogOpen(true);
  };

  // Save flex credit package changes
  const handleSavePackage = async () => {
    try {
      // Debug admin status
      console.log('Current user admin status:', isAdmin);
      
      if (!isAdmin) {
        alert('You must be an admin to save flex credit packages');
        return;
      }
      
      // Get reference to the package document
      const packageRef = doc(db, 'flexCreditPackages', editPackageType);
      
      // Create the updated package object
      const updatedPackage = {
        name: editPackageName,
        credits: parseInt(editPackageCredits),
        price: parseFloat(editPackagePrice),
        enabled: editPackageEnabled,
        stripeProductId: editPackageStripeProductId || '',
        stripePriceId: editPackageStripePriceId || '',
        type: editPackageType,
        description: editPackageDescription || '',
        creditCountText: editPackageCreditCountText || '',
        creditCountExplanation: editPackageCreditCountExplanation || '',
        sortOrder: parseInt(editPackageType.split('_')[1]) || 1,
        popular: false, // Add default value for popular field
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      console.log('Saving package to Firestore:', updatedPackage);
      
      try {
        // Use updateFlexCreditPackage from the service instead of direct Firestore access
        await updateFlexCreditPackage(editPackageType, updatedPackage);
        console.log('Package saved successfully through service');
      } catch (saveError) {
        console.error('Error during save operation:', saveError);
        alert(`Error during save operation: ${saveError.message}`);
        return;
      }
      
      // Close dialog
      setEditPackageDialogOpen(false);
      
      // Refresh the flex credit packages
      await fetchFlexCreditPackages();
    } catch (error) {
      console.error('Error updating flex credit package:', error);
      alert('Error saving package: ' + error.message);
    }
  };

  // Open purchase flex credits dialog
  const handlePurchaseFlexPackage = (user) => {
    setEditUser(user);
    setSelectedPackageType(FLEX_CREDIT_TYPES.FLEX_1);
    setPurchasePackageDialogOpen(true);
  };

  // Save flex credits purchase
  const handleSavePurchase = async () => {
    try {
      const result = await purchaseFlexCreditPackage(editUser.id, selectedPackageType);
      if (result.success) {
        setPurchasePackageDialogOpen(false);
        await fetchUsers();
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error purchasing flex credits:', error);
    }
  };

  const fetchLogs = async () => {
    try {
      const logsRef = collection(db, 'logs');
      let query = logsRef;
      
      // Apply filters
      if (logFilters.level) {
        query = query.where('level', '==', logFilters.level);
      }
      if (logFilters.dateFrom) {
        query = query.where('timestamp', '>=', new Date(logFilters.dateFrom));
      }
      if (logFilters.dateTo) {
        query = query.where('timestamp', '<=', new Date(logFilters.dateTo));
      }
      
      const snapshot = await getDocs(query);
      const logsData = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp?.toDate()
        }))
        .filter(log => {
          if (!logFilters.search) return true;
          const searchLower = logFilters.search.toLowerCase();
          const data = typeof log.data === 'string' ? log.data : JSON.stringify(log.data);
          return data.toLowerCase().includes(searchLower) ||
                 log.userId.toLowerCase().includes(searchLower) ||
                 log.shopId.toLowerCase().includes(searchLower);
        })
        .sort((a, b) => b.timestamp - a.timestamp);
      
      setLogs(logsData);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  const renderLogs = () => {
    return (
      <Card>
        <Card.Section>
          <Stack vertical>
            <Stack distribution="equalSpacing">
              <Stack.Item fill>
                <TextField
                  label="Search"
                  value={logFilters.search}
                  onChange={(value) => setLogFilters(prev => ({ ...prev, search: value }))}
                  placeholder="Search in logs..."
                />
              </Stack.Item>
              <Stack.Item>
                <Select
                  label="Log Level"
                  options={[
                    {label: 'All', value: ''},
                    {label: 'Error', value: 'ERROR'},
                    {label: 'Warning', value: 'WARN'},
                    {label: 'Info', value: 'INFO'},
                    {label: 'Debug', value: 'DEBUG'}
                  ]}
                  value={logFilters.level}
                  onChange={(value) => setLogFilters(prev => ({ ...prev, level: value }))}
                />
              </Stack.Item>
              <Stack.Item>
                <TextField
                  label="From Date"
                  type="date"
                  value={logFilters.dateFrom}
                  onChange={(value) => setLogFilters(prev => ({ ...prev, dateFrom: value }))}
                />
              </Stack.Item>
              <Stack.Item>
                <TextField
                  label="To Date"
                  type="date"
                  value={logFilters.dateTo}
                  onChange={(value) => setLogFilters(prev => ({ ...prev, dateTo: value }))}
                />
              </Stack.Item>
            </Stack>
          </Stack>
        </Card.Section>
        <ResourceList
          items={logs}
          renderItem={(log) => (
            <ResourceItem id={log.id}>
              <Stack vertical spacing="tight">
                <Stack distribution="equalSpacing">
                  <TextStyle variation="strong">
                    {new Date(log.timestamp).toLocaleString()}
                  </TextStyle>
                  <Badge status={
                    log.level === 'ERROR' ? 'critical' :
                    log.level === 'WARN' ? 'warning' :
                    log.level === 'INFO' ? 'info' : 'new'
                  }>
                    {log.level}
                  </Badge>
                </Stack>
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {typeof log.data === 'string' ? log.data : JSON.stringify(log.data, null, 2)}
                </div>
                <Stack distribution="equalSpacing">
                  <TextStyle variation="subdued">User: {log.userId}</TextStyle>
                  <TextStyle variation="subdued">Shop: {log.shopId}</TextStyle>
                </Stack>
              </Stack>
            </ResourceItem>
          )}
        />
      </Card>
    );
  };

  const fetchReferralPartners = async () => {
    try {
      setLoading(true);
      
      // Get referral partners from Firestore
      const partnersRef = collection(db, 'referralSystem');
      const snapshot = await getDocs(partnersRef);
      
      // Map the documents to an array of referral partners
      const partners = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort partners by name
      partners.sort((a, b) => {
        if (a.partnerName && b.partnerName) {
          return a.partnerName.localeCompare(b.partnerName);
        }
        return 0;
      });
      
      // Update state
      setReferralPartners(partners);
      setFilteredReferrals(partners);
      
      // Fetch referral statistics and calculate pending commissions for each partner
      const stats = {};
      const pendingPayoutsData = {};
      
      for (const partner of partners) {
        // Get the partner's referred users from the referralSystem document
        const referredUsers = partner.referredUsers || [];
        
        // Calculate pending commissions based on active subscriptions
        let pendingAmount = 0;
        
        // Count active subscriptions and calculate commissions
        const activeSubscriptions = referredUsers.filter(user => {
          if (user.subscription && user.subscription.active) {
            // Calculate commission based on subscription price and partner's commission rate
            const planPrice = user.subscription.planPrice || 0;
            
            let commissionRate;
            if (user.subscription.commissionRate !== undefined && user.subscription.commissionRate !== null) {
              // Make sure to parse the commission rate as a number
              const storedRate = parseFloat(user.subscription.commissionRate);
              if (!isNaN(storedRate)) {
                commissionRate = storedRate / 100;
              } else {
                commissionRate = partner.commission / 100;
              }
            } else {
              commissionRate = partner.commission / 100;
            }
            
            const userCommission = planPrice * commissionRate;
            pendingAmount += userCommission;
            
            // Add renewal commissions if they exist
            if (user.renewals && Array.isArray(user.renewals)) {
              user.renewals.forEach(renewal => {
                const renewalPrice = renewal.planPrice || planPrice;
                
                let renewalCommissionRate;
                if (renewal.commissionRate !== undefined && renewal.commissionRate !== null) {
                  // Make sure to parse the commission rate as a number
                  const storedRate = parseFloat(renewal.commissionRate);
                  if (!isNaN(storedRate)) {
                    renewalCommissionRate = storedRate / 100;
                  } else {
                    renewalCommissionRate = commissionRate; // Fall back to the rate used for the initial subscription
                  }
                } else {
                  renewalCommissionRate = commissionRate; // Fall back to the rate used for the initial subscription
                }
                
                const renewalCommission = renewalPrice * renewalCommissionRate;
                pendingAmount += renewalCommission;
              });
            }
            
            return true;
          } else {
            return false;
          }
        });
        
        stats[partner.id] = {
          totalReferred: referredUsers.length,
          activeSubscriptions
        };
        
        // Store pending commission amount
        pendingPayoutsData[partner.id] = pendingAmount;
      }
      
      setReferralStats(stats);
      setPendingPayouts(pendingPayoutsData);
    } catch (error) {
      console.error('Error fetching referral partners:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateReferralCode = (name) => {
    // Create a code based on name (first 3 chars) + random 5 chars
    const namePrefix = name.replace(/[^a-zA-Z0-9]/g, '').substring(0, 3).toUpperCase();
    const randomChars = Math.random().toString(36).substring(2, 7).toUpperCase();
    return `${namePrefix}${randomChars}`;
  };

  async function handleCreateReferralPartner() {
    // If a user is selected, use their information, otherwise use the form fields
    const partnerName = selectedUser ? selectedUser.fullName : newReferralName;
    const partnerEmail = selectedUser ? selectedUser.email : newReferralEmail;
    
    if (!partnerName || !partnerEmail || !newReferralCommission) {
      alert('Please fill in all fields');
      return;
    }

    if (useManualCode && !manualReferralCode) {
      alert('Please enter a referral code or disable manual code entry');
      return;
    }

    try {
      setLoading(true);
      
      // Generate a unique referral code based on the name or use manual code
      const code = useManualCode ? manualReferralCode : generateReferralCode(partnerName);
      
      // Check if the code already exists in the referral system
      const referralSystemRef = collection(db, 'referralSystem');
      const codeQuery = query(referralSystemRef, where('code', '==', code));
      const codeSnapshot = await getDocs(codeQuery);
      
      if (!codeSnapshot.empty) {
        throw new Error('This referral code already exists. Please try a different code.');
      }
      
      // If a user is selected, create a referral system document for them
      if (selectedUser) {
        // Update the user document to mark them as a referral partner
        const userRef = doc(db, 'users', selectedUser.id);
        await updateDoc(userRef, {
          isReferralPartner: true,
          partnerReferralCode: code,
          updatedAt: new Date().toISOString()
        });
        console.log('User document updated successfully as referral partner');
        
        // Create a referral system document for this partner
        const partnerDocRef = doc(db, 'referralSystem', selectedUser.id);
        await setDoc(partnerDocRef, {
          partnerName: partnerName,
          partnerEmail: partnerEmail,
          partnerId: selectedUser.id,
          code: code,
          commission: parseInt(newReferralCommission),
          isActive: true,
          referredUsers: [],
          referralStats: { total: 0, active: 0 },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        console.log('Created referral system document for partner');
      } else {
        // For partners without user accounts, create a document with a generated ID
        const partnerData = {
          partnerName: partnerName,
          partnerEmail: partnerEmail,
          code: code,
          commission: parseInt(newReferralCommission),
          isActive: true,
          referredUsers: [],
          referralStats: { total: 0, active: 0 },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        await addDoc(referralSystemRef, partnerData);
        console.log('Created referral system document for external partner');
      }
      
      // Reset form and close dialog
      setNewReferralName('');
      setNewReferralEmail('');
      setNewReferralCommission('10');
      setSelectedUser(null);
      setReferralSearchValue('');
      setManualReferralCode('');
      setUseManualCode(false);
      setCreateReferralDialogOpen(false);
      
      // Refresh the referral partners list
      fetchReferralPartners();
    } catch (error) {
      console.error('Error creating referral partner:', error);
      alert('Failed to create referral partner: ' + error.message);
    } finally {
      setLoading(false);
    }
  }

  const handleEditReferralPartner = (partner) => {
    setEditReferralPartner(partner);
    setNewReferralName(partner.partnerName || partner.name || '');
    setNewReferralEmail(partner.partnerEmail || partner.email || '');
    setNewReferralCommission(partner.commission?.toString() || '10');
    setManualReferralCode(partner.code || '');
    setUseManualCode(true); // Always enable manual code editing for existing partners
    
    // If partner is linked to a user, fetch and select that user
    if (partner.partnerId || partner.userId) {
      const fetchLinkedUser = async () => {
        try {
          const userRef = doc(db, 'users', partner.partnerId || partner.userId);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            const userData = {
              id: userDoc.id,
              ...userDoc.data()
            };
            setSelectedUser(userData);
            setReferralSearchValue(userData.email || '');
          }
        } catch (error) {
          console.error('Error fetching linked user:', error);
        }
      };
      
      fetchLinkedUser();
    } else {
      setSelectedUser(null);
      setReferralSearchValue('');
    }
    
    setEditReferralDialogOpen(true);
  };

  const handleSaveReferralPartner = async () => {
    if (!newReferralName || !newReferralEmail) {
      alert('Please enter a name and email for the referral partner');
      return;
    }

    if (useManualCode && !manualReferralCode) {
      alert('Please enter a referral code or disable manual code entry');
      return;
    }

    try {
      setLoading(true);
      
      // Get a reference to the partner document
      const partnerRef = doc(db, 'referralSystem', editReferralPartner.id);
      
      // Prepare the update data
      const updateData = {
        partnerName: newReferralName,
        partnerEmail: newReferralEmail,
        commission: parseFloat(newReferralCommission),
        updatedAt: new Date().toISOString()
      };
      
      // Check if the referral code has changed
      const codeChanged = manualReferralCode !== editReferralPartner.code;
      if (codeChanged) {
        updateData.code = manualReferralCode;
      }
      
      // Handle user linking/unlinking
      const previousUserId = editReferralPartner.partnerId || editReferralPartner.userId;
      const newUserId = selectedUser?.id;
      
      // If the user link has changed
      if (previousUserId !== newUserId) {
        // If there was a previous user, remove their referral partner status
        if (previousUserId) {
          try {
            const previousUserRef = doc(db, 'users', previousUserId);
            await updateDoc(previousUserRef, {
              isReferralPartner: false,
              partnerReferralCode: null,
              updatedAt: new Date().toISOString()
            });
            console.log('Previous user unlinked successfully');
          } catch (error) {
            console.error('Error unlinking previous user:', error);
            throw new Error('Failed to unlink previous user: ' + error.message);
          }
        }
        
        // If there's a new user to link
        if (newUserId) {
          try {
            updateData.partnerId = newUserId;
            
            const userRef = doc(db, 'users', newUserId);
            await updateDoc(userRef, {
              isReferralPartner: true,
              partnerReferralCode: manualReferralCode, // Use the potentially updated code
              updatedAt: new Date().toISOString()
            });
            console.log('New user linked successfully');
          } catch (error) {
            console.error('Error linking new user:', error);
            throw new Error('Failed to link new user: ' + error.message);
          }
        } else {
          // If no new user, remove the partnerId field
          updateData.partnerId = null;
        }
      } else if (codeChanged && previousUserId) {
        // If only the code changed and there's a linked user, update the user's referral code
        try {
          const userRef = doc(db, 'users', previousUserId);
          await updateDoc(userRef, {
            partnerReferralCode: manualReferralCode,
            updatedAt: new Date().toISOString()
          });
          console.log('Updated user referral code successfully');
        } catch (error) {
          console.error('Error updating user referral code:', error);
          throw new Error('Failed to update user referral code: ' + error.message);
        }
      }
      
      // Update the referral partner document
      await updateDoc(partnerRef, updateData);
      console.log('Referral partner updated successfully');
      
      // Reset form and close dialog
      setEditReferralPartner(null);
      setNewReferralName('');
      setNewReferralEmail('');
      setNewReferralCommission('10');
      setSelectedUser(null);
      setReferralSearchValue('');
      setManualReferralCode('');
      setUseManualCode(false);
      setEditReferralDialogOpen(false);
      
      // Refresh the referral partners list
      fetchReferralPartners();
    } catch (error) {
      console.error('Error updating referral partner:', error);
      alert('Failed to update referral partner: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleReferralStatus = async (partnerId, currentStatus) => {
    try {
      const partnerRef = doc(db, 'referralSystem', partnerId);
      await updateDoc(partnerRef, {
        isActive: !currentStatus
      });
      
      // Refresh referral partners
      await fetchReferralPartners();
    } catch (error) {
      console.error('Error toggling referral partner status:', error);
    }
  };

  const handleDeleteReferralPartner = async (partnerId) => {
    try {
      if (!window.confirm('Are you sure you want to delete this referral partner?')) {
        return;
      }
      
      setLoading(true);
      
      // Get the partner data before deleting
      const partnerRef = doc(db, 'referralSystem', partnerId);
      const partnerDoc = await getDoc(partnerRef);
      
      if (!partnerDoc.exists()) {
        throw new Error('Referral partner not found');
      }
      
      const partnerData = partnerDoc.data();
      
      // If the partner is linked to a user, update the user document
      if (partnerData.partnerId || partnerData.userId) {
        try {
          const userRef = doc(db, 'users', partnerData.partnerId || partnerData.userId);
          await updateDoc(userRef, {
            isReferralPartner: false,
            partnerReferralCode: null,
            updatedAt: new Date().toISOString()
          });
          console.log('User unlinked successfully');
        } catch (error) {
          console.error('Error unlinking user:', error);
          throw new Error('Failed to unlink user: ' + error.message);
        }
      }
      
      // Delete the referral partner document
      await deleteDoc(partnerRef);
      console.log('Referral partner deleted successfully');
      
      // Refresh the referral partners list
      fetchReferralPartners();
    } catch (error) {
      console.error('Error deleting referral partner:', error);
      alert('Failed to delete referral partner: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const filterReferralPartners = () => {
    if (!referralPartners) return [];
    
    const stats = {};
    
    // Calculate stats for each partner
    referralPartners.forEach(partner => {
      const referredUsers = partner.referredUsers || [];
      let pendingAmount = 0;
      
      // Count active subscriptions and calculate pending commissions
      const activeSubscriptions = referredUsers.filter(user => {
        if (user.subscription && user.subscription.active) {
          // Calculate commission based on subscription price and partner's commission rate
          const planPrice = user.subscription.planPrice || 0;
          
          let commissionRate;
          if (user.subscription.commissionRate !== undefined && user.subscription.commissionRate !== null) {
            // Make sure to parse the commission rate as a number
            const storedRate = parseFloat(user.subscription.commissionRate);
            if (!isNaN(storedRate)) {
              commissionRate = storedRate / 100;
            } else {
              commissionRate = partner.commission / 100;
            }
          } else {
            commissionRate = partner.commission / 100;
          }
          
          const userCommission = planPrice * commissionRate;
          pendingAmount += userCommission;
          
          // Add renewal commissions if they exist
          if (user.renewals && Array.isArray(user.renewals)) {
            user.renewals.forEach(renewal => {
              const renewalPrice = renewal.planPrice || planPrice;
              
              let renewalCommissionRate;
              if (renewal.commissionRate !== undefined && renewal.commissionRate !== null) {
                // Make sure to parse the commission rate as a number
                const storedRate = parseFloat(renewal.commissionRate);
                if (!isNaN(storedRate)) {
                  renewalCommissionRate = storedRate / 100;
                } else {
                  renewalCommissionRate = commissionRate; // Fall back to the subscription rate
                }
              } else {
                renewalCommissionRate = commissionRate; // Fall back to the subscription rate
              }
              
              const renewalCommission = renewalPrice * renewalCommissionRate;
              pendingAmount += renewalCommission;
            });
          }
          
          return true;
        }
        return false;
      }).length;
      
      stats[partner.id] = {
        totalReferred: referredUsers.length,
        activeSubscriptions,
        pendingAmount
      };
    });
    
    // Filter based on search value (case insensitive)
    const searchLower = referralSearchValue.toLowerCase();
    const filtered = referralPartners.filter(partner => 
      partner.partnerName?.toLowerCase().includes(searchLower) || 
      partner.partnerEmail?.toLowerCase().includes(searchLower) ||
      partner.code?.toLowerCase().includes(searchLower)
    );
    
    // Update the filtered referrals state
    setFilteredReferrals(filtered);
    
    // Apply pagination to filtered results
    const startIndex = (referralCurrentPage - 1) * referralsPerPage;
    const endIndex = startIndex + referralsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const copyReferralLink = (code) => {
    const referralLink = `${window.location.origin}/app/register?ref=${code}`;
    navigator.clipboard.writeText(referralLink);
    alert('Referral link copied to clipboard!');
  };

  const filterUsers = () => {
    if (!searchValue.trim() && appliedFilters.length === 0) {
      setFilteredUsers(users);
      return;
    }

    let filtered = [...users];
    
    // Apply search
    if (searchValue.trim()) {
      const searchLower = searchValue.toLowerCase();
      filtered = filtered.filter(user => {
        const email = (user.email || '').toLowerCase();
        const name = (user.displayName || user.fullName || '').toLowerCase();
        const plan = (user.subscription?.type || '').toLowerCase();
        
        return email.includes(searchLower) || 
               name.includes(searchLower) || 
               plan.includes(searchLower);
      });
    }
    
    // Apply filters
    if (appliedFilters.length > 0) {
      appliedFilters.forEach(filter => {
        if (filter.key === 'role') {
          if (filter.value === 'admin') {
            filtered = filtered.filter(user => user.isAdmin === true);
          } else if (filter.value === 'user') {
            filtered = filtered.filter(user => user.isAdmin !== true);
          }
        } else if (filter.key === 'status') {
          if (filter.value === 'active') {
            filtered = filtered.filter(user => user.isActive !== false);
          } else if (filter.value === 'inactive') {
            filtered = filtered.filter(user => user.isActive === false);
          }
        } else if (filter.key === 'subscription') {
          if (filter.value === 'subscribed') {
            filtered = filtered.filter(user => user.subscription);
          } else if (filter.value === 'unsubscribed') {
            filtered = filtered.filter(user => !user.subscription);
          } else if (filter.value === 'vip') {
            filtered = filtered.filter(user => user.isVIP);
          }
        } else if (filter.key === 'minSubscriptionDays') {
          const minDays = parseInt(filter.value);
          filtered = filtered.filter(user => {
            if (!user.subscription) return false;
            const subscriptionDate = user.subscription.startDate ? new Date(user.subscription.startDate) : null;
            const today = new Date();
            const diffDays = Math.floor((today - subscriptionDate) / (1000 * 60 * 60 * 24));
            return diffDays >= minDays;
          });
        }
      });
    }
    
    setFilteredUsers(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Handle filter removal
  const handleRemoveFilter = (key) => {
    setAppliedFilters(appliedFilters.filter(filter => filter.key !== key));
  };
  
  // Handle filter clear all
  const handleClearAll = () => {
    setAppliedFilters([]);
    setSearchValue('');
  };

  const renderUserRows = () => {
    // Get current users for pagination
    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
    
    return currentUsers.map(user => {
      // Format VIP status information
      let vipStatusDisplay = 'Regular';
      let vipStatusBadge = 'default';
      
      if (user.isVIP) {
        const planName = subscriptionPlans[user.vipPlan]?.name || user.vipPlan;
        
        if (user.vipSubscription?.endDate) {
          const endDate = new Date(user.vipSubscription.endDate);
          const formattedDate = endDate.toLocaleDateString();
          vipStatusDisplay = `VIP (${planName}) until ${formattedDate}`;
        } else {
          vipStatusDisplay = `VIP (${planName}) - Unlimited`;
        }
        
        vipStatusBadge = 'attention';
      }
      
      return [
        user.email || 'No Email',
        user.fullName || user.displayName || 'Unknown',
        <Badge status={user.isAdmin ? 'success' : 'default'}>
          {user.isAdmin ? 'Admin' : 'User'}
        </Badge>,
        <Badge status={user.isActive === false ? 'warning' : 'success'}>
          {user.isActive === false ? 'Inactive' : 'Active'}
        </Badge>,
        <Badge status={vipStatusBadge}>
          {vipStatusDisplay}
        </Badge>,
        user.credits?.monthly || 0,
        user.credits?.flex || 0,
        user.subscription?.type || 'None',
        <ButtonGroup>
          {user.isAdmin ? (
            <Button 
              size="slim" 
              destructive
              onClick={() => handleRemoveAdmin(user.id)}
            >
              Remove Admin
            </Button>
          ) : (
            <Button 
              size="slim"
              primary
              onClick={() => handleMakeAdmin(user.id)}
            >
              Make Admin
            </Button>
          )}
          {user.isActive !== false ? (
            <Button 
              size="slim"
              destructive
              onClick={() => handleDeactivateUser(user.id)}
            >
              Deactivate
            </Button>
          ) : (
            <Button 
              size="slim"
              primary
              onClick={() => handleActivateUser(user.id)}
            >
              Activate
            </Button>
          )}
          <Button 
            size="slim" 
            destructive 
            onClick={() => handleDeleteUser(user.id)}
          >
            Delete
          </Button>
          <Button 
            size="slim"
            onClick={() => handleOpenVIPDialog(user.id)}
          >
            {user.isVIP ? 'Modify VIP' : 'Add VIP'}
          </Button>
          {user.isVIP && (
            <Button 
              size="slim" 
              destructive 
              onClick={() => handleRemoveVIPStatus(user.id)}
            >
              Remove VIP
            </Button>
          )}
        </ButtonGroup>
      ];
    });
  };

  const filterCreditUsers = () => {
    if (!searchCreditValue.trim()) {
      return users;
    }
    
    const searchLower = searchCreditValue.toLowerCase();
    return users.filter(user => {
      return (
        (user.email && user.email.toLowerCase().includes(searchLower)) ||
        (user.displayName && user.displayName.toLowerCase().includes(searchLower))
      );
    });
  };

  const filteredCreditUsers = filterCreditUsers();
  const indexOfLastCreditUser = creditCurrentPage * creditsPerPage;
  const indexOfFirstCreditUser = indexOfLastCreditUser - creditsPerPage;
  const currentCreditUsers = filteredCreditUsers.slice(indexOfFirstCreditUser, indexOfLastCreditUser);

  const creditRows = currentCreditUsers.map((user) => [
    user.email,
    user.credits?.monthly || 0,
    user.credits?.flex || 0,
    user.credits?.used || 0,
    user.subscription ? (
      <Badge status="success">
        {user.subscription.type === 'plan_1' ? 'Basic' :
         user.subscription.type === 'plan_2' ? 'Standard' :
         user.subscription.type === 'plan_3' ? 'Premium' :
         user.subscription.type === 'plan_4' ? 'Enterprise' :
         user.subscription.type === 'plan_5' ? 'Ultimate' :
         user.subscription.planName || user.subscription.type} (${user.subscription.price}/mo)
      </Badge>
    ) : user.isVIP ? (
      <Badge status="info">VIP Access</Badge>
    ) : (
      <Badge>No subscription</Badge>
    ),
    <ButtonGroup>
      <Button size="slim" onClick={() => handleEditUser(user)}>
        Edit Credits
      </Button>
      <Button size="slim" onClick={() => handleAddFlexCredits(user)}>
        Edit Flex Credits
      </Button>
      <Button size="slim" onClick={() => handlePurchaseFlexPackage(user)}>
        Purchase Flex Credits
      </Button>
    </ButtonGroup>
  ]);

  const tabs = [
    {
      id: 'users',
      content: 'Users',
      accessibilityLabel: 'Users',
      panelID: 'users-panel',
    },
    {
      id: 'credits',
      content: 'Credits Management',
      accessibilityLabel: 'Credits Management',
      panelID: 'credits-panel',
    },
    {
      id: 'subscriptions',
      content: 'Subscription Plans',
      accessibilityLabel: 'Subscription Plans',
      panelID: 'subscriptions-panel',
    },
    {
      id: 'flex-credits',
      content: 'Flex Credit Packages',
      accessibilityLabel: 'Flex Credit Packages',
      panelID: 'flex-credits-panel',
    },
    {
      id: 'stripe',
      content: 'Stripe Settings',
      accessibilityLabel: 'Stripe Settings',
      panelID: 'stripe-panel',
    },
    {
      id: 'server-status',
      content: 'Server Status',
      accessibilityLabel: 'Server Status',
      panelID: 'server-status-panel',
    },
    {
      id: 'system-settings',
      content: 'System Settings',
      accessibilityLabel: 'System Settings',
      panelID: 'system-settings-panel',
    },
    {
      id: 'logs',
      content: 'Logs Viewer',
      accessibilityLabel: 'Logs Viewer',
      panelID: 'logs-panel',
    },
    {
      id: 'sales',
      content: 'Sales Management',
      accessibilityLabel: 'Sales Management',
      panelID: 'sales-panel',
    },
    {
      id: 'maintenance',
      content: 'Maintenance Management',
      accessibilityLabel: 'Maintenance Management',
      panelID: 'maintenance-panel',
    },
    {
      id: 'beta',
      content: 'Beta Management',
      accessibilityLabel: 'Beta Management',
      panelID: 'beta-panel',
    },
    {
      id: 'tracking',
      content: 'Tracking Admin',
      accessibilityLabel: 'Tracking Admin',
      panelID: 'tracking-panel',
    },
    {
      id: 'referral',
      content: 'Referral Management',
      accessibilityLabel: 'Referral Management',
      panelID: 'referral-panel',
    },
    {
      id: 'referral-payouts',
      content: 'Referral Payouts',
      accessibilityLabel: 'Referral Payouts',
      panelID: 'referral-payouts-panel',
    },
    {
      id: 'ai-models',
      content: 'AI Models',
      accessibilityLabel: 'AI Model Management',
      panelID: 'ai-models-panel',
    },
    {
      id: 'trial-plan',
      content: 'Trial Plan',
      accessibilityLabel: 'Trial Plan',
      panelID: 'trial-plan-panel',
    }
  ];

  const renderUserManagement = () => {
    // Filter configuration
    const filters = [
      {
        key: 'role',
        label: 'Role',
        filter: (
          <Select
            label="Role"
            labelHidden
            options={[
              {label: 'Admin', value: 'admin'},
              {label: 'User', value: 'user'}
            ]}
            onChange={(value) => {
              setAppliedFilters([
                ...appliedFilters.filter(filter => filter.key !== 'role'),
                {key: 'role', value: value, label: value === 'admin' ? 'Admin' : 'User'}
              ]);
            }}
            value={appliedFilters.find(filter => filter.key === 'role')?.value || ''}
          />
        ),
        shortcut: true
      },
      {
        key: 'status',
        label: 'Status',
        filter: (
          <Select
            label="Status"
            labelHidden
            options={[
              {label: 'Active', value: 'active'},
              {label: 'Inactive', value: 'inactive'}
            ]}
            onChange={(value) => {
              setAppliedFilters([
                ...appliedFilters.filter(filter => filter.key !== 'status'),
                {key: 'status', value: value, label: value === 'active' ? 'Active' : 'Inactive'}
              ]);
            }}
            value={appliedFilters.find(filter => filter.key === 'status')?.value || ''}
          />
        ),
        shortcut: true
      },
      {
        key: 'subscription',
        label: 'Subscription',
        filter: (
          <Select
            label="Subscription"
            labelHidden
            options={[
              {label: 'Subscribed', value: 'subscribed'},
              {label: 'Unsubscribed', value: 'unsubscribed'},
              {label: 'VIP', value: 'vip'}
            ]}
            onChange={(value) => {
              setAppliedFilters([
                ...appliedFilters.filter(filter => filter.key !== 'subscription'),
                {key: 'subscription', value: value, label: value.charAt(0).toUpperCase() + value.slice(1)}
              ]);
            }}
            value={appliedFilters.find(filter => filter.key === 'subscription')?.value || ''}
          />
        ),
        shortcut: true
      },
      {
        key: 'minSubscriptionDays',
        label: 'Minimum Subscription Age',
        filter: (
          <Select
            label="Minimum Subscription Age"
            labelHidden
            options={[
              {label: 'All (No filter)', value: '0'},
              {label: '30 Days', value: '30'},
              {label: '60 Days', value: '60'},
              {label: '90 Days', value: '90'}
            ]}
            onChange={(value) => {
              setAppliedFilters([
                ...appliedFilters.filter(filter => filter.key !== 'minSubscriptionDays'),
                {key: 'minSubscriptionDays', value: value, label: value === '0' ? 'All (No filter)' : `${value} Days`}
              ]);
            }}
            value={appliedFilters.find(filter => filter.key === 'minSubscriptionDays')?.value || ''}
          />
        ),
        shortcut: true
      }
    ];
    
    return (
      <>
        <Card sectioned>
          <Filters
            queryValue={searchValue}
            filters={filters}
            appliedFilters={appliedFilters.map(filter => ({
              key: filter.key,
              label: filter.label,
              onRemove: () => handleRemoveFilter(filter.key)
            }))}
            onQueryChange={setSearchValue}
            onQueryClear={() => setSearchValue('')}
            onClearAll={handleClearAll}
          >
            <div style={{ paddingLeft: '8px' }}>
              <Button 
                icon={<Icon source={SearchMinor} />}
                onClick={filterUsers}
              >
                Search
              </Button>
            </div>
          </Filters>
        </Card>
        <Card>
          <DataTable
            columnContentTypes={[
              'text',
              'text',
              'text',
              'text',
              'text',
              'numeric',
              'numeric',
              'text',
              'text'
            ]}
            headings={[
              'Email',
              'Name',
              'Role',
              'Status',
              'VIP Status',
              'Monthly Credits',
              'Flex Credits',
              'Subscription',
              'Actions'
            ]}
            rows={renderUserRows()}
          />
        </Card>
        
        <Card sectioned>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Pagination
              hasPrevious={currentPage > 1}
              onPrevious={() => setCurrentPage(currentPage - 1)}
              hasNext={currentPage * usersPerPage < filteredUsers.length}
              onNext={() => setCurrentPage(currentPage + 1)}
            />
          </div>
          <div style={{ textAlign: 'center', marginTop: '10px' }}>
            <TextStyle variation="subdued">
              Showing {Math.min((currentPage - 1) * usersPerPage + 1, filteredUsers.length)} - {Math.min(currentPage * usersPerPage, filteredUsers.length)} of {filteredUsers.length} users
            </TextStyle>
          </div>
        </Card>
      </>
    );
  };

  if (!isAdmin) {
    return <Navigate to="/app" replace />;
  }

  // Migrate old subscription types to new ones
  const migrateSubscriptionTypes = async () => {
    try {
      const count = await migrateOldSubscriptionTypes();
      if (count > 0) {
        alert(`Successfully migrated ${count} users to new subscription types`);
        await fetchUsers(); // Refresh user list
      }
    } catch (error) {
      console.error('Error during subscription migration:', error);
    }
  };

  // Handle editing Stripe settings
  const handleEditStripeSettings = () => {
    setEditStripeApiKey(stripeSettings.apiKey || '');
    setEditStripePublishableKey(stripeSettings.publishableKey || '');
    setEditStripeWebhookSecret(stripeSettings.webhookSecret || '');
    setEditStripeTestMode(stripeSettings.testMode !== false);
    setEditStripeSettingsDialogOpen(true);
  };
  
  // Save Stripe settings
  const handleSaveStripeSettings = async () => {
    try {
      await updateStripeSettings({
        apiKey: editStripeApiKey,
        publishableKey: editStripePublishableKey,
        webhookSecret: editStripeWebhookSecret,
        testMode: editStripeTestMode
      });
      setEditStripeSettingsDialogOpen(false);
      await fetchStripeSettings();
    } catch (error) {
      console.error('Error updating Stripe settings:', error);
    }
  };

  // Handle setting VIP status for a user
  const handleSetVIPStatus = async () => {
    try {
      const duration = vipDurationType === 'unlimited' ? 'unlimited' : parseInt(vipMonths);
      await setUserVIPStatus(vipUserId, vipPlanType, duration);
      setVipDialogOpen(false);
      await fetchUsers();
    } catch (error) {
      console.error('Error setting VIP status:', error);
    }
  };

  // Handle removing VIP status from a user
  const handleRemoveVIPStatus = async (userId) => {
    if (window.confirm('Are you sure you want to remove VIP status from this user?')) {
      try {
        await removeUserVIPStatus(userId);
        await fetchUsers();
      } catch (error) {
        console.error('Error removing VIP status:', error);
      }
    }
  };

  // Open VIP dialog for a user
  const handleOpenVIPDialog = (userId) => {
    setVipUserId(userId);
    setVipPlanType(SUBSCRIPTION_TYPES.PLAN_3); // Default to Premium plan
    setVipDurationType('unlimited');
    setVipMonths(3);
    setVipDialogOpen(true);
  };

  // VIP Status Dialog
  const vipStatusDialog = (
    <Modal
      open={vipDialogOpen}
      onClose={() => setVipDialogOpen(false)}
      title={`Set VIP Status for ${vipUserId}`}
      primaryAction={{
        content: 'Set VIP Status',
        onAction: handleSetVIPStatus
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: () => setVipDialogOpen(false)
        }
      ]}
    >
      <Modal.Section>
        <FormLayout>
          <Select
            label="VIP Plan"
            options={Object.entries(subscriptionPlans).map(([key, plan]) => ({
              label: `${plan.name} ($${plan.price}/mo - ${plan.monthlyCredits} credits)`,
              value: key
            }))}
            value={vipPlanType}
            onChange={setVipPlanType}
          />
          
          <Select
            label="Duration Type"
            options={[
              {label: 'Unlimited', value: 'unlimited'},
              {label: 'Limited Months', value: 'limited'}
            ]}
            value={vipDurationType}
            onChange={setVipDurationType}
          />
          
          {vipDurationType === 'limited' && (
            <TextField
              label="Number of Months"
              type="number"
              value={vipMonths.toString()}
              onChange={(value) => setVipMonths(parseInt(value) || 1)}
              min={1}
              autoComplete="off"
            />
          )}
          
          <Banner status="info">
            <p>VIP status gives the user access to all features of the selected plan without requiring a regular subscription. {vipDurationType === 'limited' ? `This VIP status will automatically expire after ${vipMonths} month${vipMonths === 1 ? '' : 's'}.` : 'This VIP status will never expire unless manually removed.'}</p>
          </Banner>
        </FormLayout>
      </Modal.Section>
    </Modal>
  );

  const applySubscriptionSale = async () => {
    try {
      if (!saleName || subscriptionSalePercentage <= 0) {
        alert('Please enter a valid sale name and percentage');
        return;
      }
      
      // Update the sales document
      const salesRef = doc(db, 'settings', 'sales');
      await setDoc(salesRef, {
        subscription: {
          active: true,
          name: saleName,
          percentage: subscriptionSalePercentage
        },
        credits: activeSales.credits
      }, { merge: true });
      
      // Apply the sale to all subscription plans
      const plansRef = collection(db, 'subscriptionPlans');
      const plansSnapshot = await getDocs(plansRef);
      
      const batch = writeBatch(db);
      
      plansSnapshot.forEach((planDoc) => {
        const planData = planDoc.data();
        const price = planData.price;
        
        // Calculate compareAtPrice based on salePercentage
        const priceCents = Math.round((price % 1) * 100);
        const rawComparePrice = price / (1 - subscriptionSalePercentage / 100);
        const formattedPrice = Math.floor(rawComparePrice) + (priceCents / 100);
        const compareAtPrice = parseFloat(formattedPrice.toFixed(2));
        
        batch.update(planDoc.ref, {
          compareAtPrice,
          salePercentage: subscriptionSalePercentage
        });
      });
      
      await batch.commit();
      
      // Refresh data
      await fetchActiveSales();
      await fetchSubscriptionPlans();
      
      setSalesDialogOpen(false);
      alert('Subscription sale applied successfully!');
    } catch (error) {
      console.error('Error applying subscription sale:', error);
      alert('Error applying sale: ' + error.message);
    }
  };

  const applyCreditsSale = async () => {
    try {
      if (!saleName || creditsSalePercentage <= 0) {
        alert('Please enter a valid sale name and percentage');
        return;
      }
      
      // Update the sales document
      const salesRef = doc(db, 'settings', 'sales');
      await setDoc(salesRef, {
        subscription: activeSales.subscription,
        credits: {
          active: true,
          name: saleName,
          percentage: creditsSalePercentage
        }
      }, { merge: true });
      
      // Apply the sale to all credit packages
      const packagesRef = collection(db, 'flexCreditPackages');
      const packagesSnapshot = await getDocs(packagesRef);
      
      const batch = writeBatch(db);
      
      packagesSnapshot.forEach((packageDoc) => {
        const packageData = packageDoc.data();
        const price = packageData.price;
        
        // Calculate compareAtPrice based on salePercentage
        const priceCents = Math.round((price % 1) * 100);
        const rawComparePrice = price / (1 - creditsSalePercentage / 100);
        const formattedPrice = Math.floor(rawComparePrice) + (priceCents / 100);
        const compareAtPrice = parseFloat(formattedPrice.toFixed(2));
        
        batch.update(packageDoc.ref, {
          compareAtPrice,
          salePercentage: creditsSalePercentage
        });
      });
      
      await batch.commit();
      
      // Refresh data
      await fetchActiveSales();
      await fetchFlexCreditPackages();
      
      setSalesDialogOpen(false);
      alert('Credits sale applied successfully!');
    } catch (error) {
      console.error('Error applying credits sale:', error);
      alert('Error applying sale: ' + error.message);
    }
  };

  const removeSubscriptionSale = async () => {
    try {
      if (!window.confirm('Are you sure you want to remove the subscription sale?')) {
        return;
      }
      
      // Update the sales document
      const salesRef = doc(db, 'settings', 'sales');
      await setDoc(salesRef, {
        subscription: {
          active: false,
          name: '',
          percentage: 0
        },
        credits: activeSales.credits
      }, { merge: true });
      
      // Remove sale from all subscription plans
      const plansRef = collection(db, 'subscriptionPlans');
      const plansSnapshot = await getDocs(plansRef);
      
      const batch = writeBatch(db);
      
      plansSnapshot.forEach((planDoc) => {
        batch.update(planDoc.ref, {
          compareAtPrice: null,
          salePercentage: 0
        });
      });
      
      await batch.commit();
      
      // Refresh data
      await fetchActiveSales();
      await fetchSubscriptionPlans();
      
      alert('Subscription sale removed successfully!');
    } catch (error) {
      console.error('Error removing subscription sale:', error);
      alert('Error removing sale: ' + error.message);
    }
  };

  const removeCreditsSale = async () => {
    try {
      if (!window.confirm('Are you sure you want to remove the credits sale?')) {
        return;
      }
      
      // Update the sales document
      const salesRef = doc(db, 'settings', 'sales');
      await setDoc(salesRef, {
        subscription: activeSales.subscription,
        credits: {
          active: false,
          name: '',
          percentage: 0
        }
      }, { merge: true });
      
      // Remove sale from all credit packages
      const packagesRef = collection(db, 'flexCreditPackages');
      const packagesSnapshot = await getDocs(packagesRef);
      
      const batch = writeBatch(db);
      
      packagesSnapshot.forEach((packageDoc) => {
        batch.update(packageDoc.ref, {
          compareAtPrice: null,
          salePercentage: 0
        });
      });
      
      await batch.commit();
      
      // Refresh data
      await fetchActiveSales();
      await fetchFlexCreditPackages();
      
      alert('Credits sale removed successfully!');
    } catch (error) {
      console.error('Error removing credits sale:', error);
      alert('Error removing sale: ' + error.message);
    }
  };

  const subscriptionPlanRows = Object.entries(subscriptionPlans)
    .sort((a, b) => {
      // Sort by sortOrder property
      return (a[1].sortOrder || 999) - (b[1].sortOrder || 999);
    })
    .map(([type, plan]) => [
      plan.name,
      `$${plan.price}`,
      plan.monthlyCredits,
      plan.enabled ? 'Enabled' : 'Disabled',
      <Button size="slim" onClick={() => handleEditPlan(type)}>Edit</Button>
    ]);

  const flexCreditPackageRows = Object.entries(flexCreditPackages)
    .sort((a, b) => {
      // Sort by sortOrder property
      return (a[1].sortOrder || 999) - (b[1].sortOrder || 999);
    })
    .map(([type, packageData]) => [
      packageData.name,
      `$${packageData.price}`,
      packageData.credits,
      packageData.enabled ? 'Enabled' : 'Disabled',
      <Button size="slim" onClick={() => handleEditPackage(type)}>Edit</Button>
    ]);

  // Fetch maintenance info
  const fetchMaintenanceInfo = async () => {
    try {
      setMaintenanceLoading(true);
      
      // Fetch maintenance info from Firestore
      const info = await SystemSettingsService.getMaintenanceInfo();
      setMaintenanceInfo(info);
      
      // If there's a scheduled maintenance, set the form fields
      if (info.scheduledMaintenanceDate) {
        const startDate = info.scheduledMaintenanceDate.toDate();
        setMaintenanceStartDate(startDate.toISOString().split('T')[0]);
        setMaintenanceStartTime(
          `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`
        );
        
        if (info.scheduledMaintenanceEndDate) {
          const endDate = info.scheduledMaintenanceEndDate.toDate();
          setMaintenanceEndDate(endDate.toISOString().split('T')[0]);
          setMaintenanceEndTime(
            `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`
          );
        }
        
        setMaintenanceTitle(info.maintenanceTitle || 'System Maintenance');
        setMaintenanceMessage(info.maintenanceMessage || '');
      }
    } catch (error) {
      console.error('Error fetching maintenance info:', error);
    } finally {
      setMaintenanceLoading(false);
    }
  };

  // Handle scheduling maintenance
  const handleScheduleMaintenance = async () => {
    try {
      // Validate inputs
      if (!maintenanceStartDate || !maintenanceStartTime) {
        alert('Please enter a valid start date and time');
        return;
      }
      
      // Parse dates
      const startDateTime = new Date(`${maintenanceStartDate}T${maintenanceStartTime}`);
      let endDateTime = null;
      
      if (maintenanceEndDate && maintenanceEndTime) {
        endDateTime = new Date(`${maintenanceEndDate}T${maintenanceEndTime}`);
        
        // Validate end date is after start date
        if (endDateTime <= startDateTime) {
          alert('End time must be after start time');
          return;
        }
      }
      
      // Schedule maintenance
      await SystemSettingsService.scheduleMaintenance(
        startDateTime,
        endDateTime,
        maintenanceTitle,
        maintenanceMessage,
        user.uid
      );
      
      // Refresh maintenance info
      await fetchMaintenanceInfo();
      
      // Close dialog
      setMaintenanceDialogOpen(false);
    } catch (error) {
      console.error('Error scheduling maintenance:', error);
      alert(`Error scheduling maintenance: ${error.message}`);
    }
  };

  // Handle starting maintenance immediately
  const handleStartMaintenance = async () => {
    try {
      await SystemSettingsService.startMaintenance(user.uid);
      await fetchMaintenanceInfo();
    } catch (error) {
      console.error('Error starting maintenance:', error);
      alert(`Error starting maintenance: ${error.message}`);
    }
  };

  // Handle ending maintenance
  const handleEndMaintenance = async () => {
    try {
      await SystemSettingsService.endMaintenance(user.uid);
      await fetchMaintenanceInfo();
    } catch (error) {
      console.error('Error ending maintenance:', error);
      alert(`Error ending maintenance: ${error.message}`);
    }
  };

  // Handle canceling scheduled maintenance
  const handleCancelScheduledMaintenance = async () => {
    try {
      await SystemSettingsService.cancelScheduledMaintenance(user.uid);
      await fetchMaintenanceInfo();
    } catch (error) {
      console.error('Error canceling scheduled maintenance:', error);
      alert(`Error canceling scheduled maintenance: ${error.message}`);
    }
  };

  const renderMaintenanceManagement = () => {
    return (
      <Card>
        <Card.Section>
          <Banner status="info">
            <p>Maintenance management allows you to schedule or start maintenance for your system.</p>
          </Banner>
        </Card.Section>
        <Card.Section>
          <ButtonGroup>
            <Button
              primary
              onClick={() => setMaintenanceDialogOpen(true)}
            >
              Schedule Maintenance
            </Button>
            <Button
              primary
              onClick={handleStartMaintenance}
            >
              Start Maintenance Now
            </Button>
          </ButtonGroup>
        </Card.Section>
        {maintenanceInfo && maintenanceInfo.scheduledMaintenanceDate && (
          <Card.Section>
            <Banner status="success">
              <p><strong>Scheduled maintenance:</strong></p>
              <ul>
                <li>
                  <strong>Title:</strong> {maintenanceInfo.maintenanceTitle}
                </li>
                <li>
                  <strong>Message:</strong> {maintenanceInfo.maintenanceMessage}
                </li>
                <li>
                  <strong>Start Date:</strong> {maintenanceInfo.scheduledMaintenanceDate.toDate().toLocaleString()}
                </li>
                {maintenanceInfo.scheduledMaintenanceEndDate && (
                  <li>
                    <strong>End Date:</strong> {maintenanceInfo.scheduledMaintenanceEndDate.toDate().toLocaleString()}
                  </li>
                )}
              </ul>
              <div style={{ marginTop: '10px' }}>
                <ButtonGroup>
                  <Button 
                    destructive
                    onClick={handleCancelScheduledMaintenance}
                  >
                    Cancel Scheduled Maintenance
                  </Button>
                </ButtonGroup>
              </div>
            </Banner>
          </Card.Section>
        )}
        {maintenanceInfo && maintenanceInfo.maintenanceMode && (
          <Card.Section>
            <Banner status="critical">
              <p>Maintenance mode is currently active.</p>
              <div style={{ marginTop: '10px' }}>
                <ButtonGroup>
                  <Button
                    primary
                    onClick={handleEndMaintenance}
                  >
                    End Maintenance
                  </Button>
                </ButtonGroup>
              </div>
            </Banner>
          </Card.Section>
        )}
      </Card>
    );
  };

  const renderReferralManagement = () => {
    // Calculate paginated referrals
    const startIndex = (referralCurrentPage - 1) * referralsPerPage;
    const endIndex = startIndex + referralsPerPage;
    const paginatedReferrals = filteredReferrals.slice(startIndex, endIndex);
    
    return (
      <Card>
        <Card.Section>
          <Banner status="info">
            <p>Manage referral partners and their stats here. Referral partners earn commission on subscriptions from users they refer.</p>
          </Banner>
        </Card.Section>
        
        <Card.Section>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Button primary onClick={openCreateReferralDialog}>
                Create Referral Partner
              </Button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ marginRight: '1rem' }}>
                <TextField
                  label=""
                  value={referralSearchValue}
                  onChange={setReferralSearchValue}
                  placeholder="Search by name or email"
                  clearButton
                  onClearButtonClick={() => setReferralSearchValue('')}
                />
              </div>
            </div>
          </div>
          
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <Spinner size="large" />
            </div>
          ) : referralPartners.length === 0 ? (
            <EmptyState
              heading="No referral partners yet"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>Create your first referral partner to start tracking referrals.</p>
            </EmptyState>
          ) : (
            <>
              <DataTable
                columnContentTypes={[
                  'text',
                  'text',
                  'text',
                  'text',
                  'numeric',
                  'numeric',
                  'text',
                  'text'
                ]}
                headings={[
                  'Partner',
                  'Email',
                  'User Account',
                  'Referral Code',
                  'Referrals',
                  'Commission',
                  'Status',
                  'Actions'
                ]}
                rows={paginatedReferrals.map(partner => [
                  partner.partnerName || partner.name || 'Unknown',
                  partner.partnerEmail || partner.email || 'Unknown',
                  partner.partnerId || partner.userId ? (
                    <Link url={`#user-${partner.partnerId || partner.userId}`} onClick={() => {
                      setSelectedTab(0); // Switch to User Management tab
                      // Scroll to user in the list or highlight them
                    }}>
                      Linked Account
                    </Link>
                  ) : 'No linked account',
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '0.5rem' }}>{partner.code}</span>
                    <Button plain icon={DuplicateMinor} onClick={() => copyReferralLink(partner.code)} />
                  </div>,
                  referralStats[partner.id]?.totalReferred || 0,
                  `${partner.commission || 0}%`,
                  partner.isActive !== false ? (
                    <Badge status="success">Active</Badge>
                  ) : (
                    <Badge>Inactive</Badge>
                  ),
                  <ButtonGroup>
                    <Button size="slim" onClick={() => handleEditReferralPartner(partner)}>
                      Edit
                    </Button>
                    <Button 
                      size="slim"
                      onClick={() => handleToggleReferralStatus(partner.id, partner.isActive !== false)}
                    >
                      {partner.isActive !== false ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button 
                      size="slim" 
                      destructive 
                      onClick={() => handleDeleteReferralPartner(partner.id)}
                    >
                      Delete
                    </Button>
                  </ButtonGroup>
                ])}
              />
              
              <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  hasPrevious={referralCurrentPage > 1}
                  onPrevious={() => setReferralCurrentPage(referralCurrentPage - 1)}
                  hasNext={referralCurrentPage * referralsPerPage < filteredReferrals.length}
                  onNext={() => setReferralCurrentPage(referralCurrentPage + 1)}
                />
              </div>
              
              <div style={{ textAlign: 'center', marginTop: '10px' }}>
                <TextStyle variation="subdued">
                  Showing {Math.min((referralCurrentPage - 1) * referralsPerPage + 1, filteredReferrals.length)} - {Math.min(referralCurrentPage * referralsPerPage, filteredReferrals.length)} of {filteredReferrals.length} referral partners
                </TextStyle>
              </div>
            </>
          )}
        </Card.Section>
        <Card title="Referral Bonus Settings" sectioned>
          <Stack vertical spacing="loose">
            <TextContainer>
              <p>Configure the bonus flex credits awarded to new users who register through a referral link.</p>
            </TextContainer>
            
            <Stack alignment="center" distribution="equalSpacing">
              <Stack.Item>
                <TextStyle variation="strong">Current Bonus: {referralBonusSettings} Flex Credits</TextStyle>
              </Stack.Item>
              <Stack.Item>
                <Button onClick={() => setShowReferralBonusModal(true)}>Edit Bonus Amount</Button>
              </Stack.Item>
            </Stack>
            
            <Banner status="info">
              <p>These credits will be automatically added to new user accounts when they register using a referral link.</p>
            </Banner>
          </Stack>
        </Card>
      </Card>
    );
  };

  const handleUserSearch = async (value) => {
    setReferralSearchValue(value);
    
    if (!value.trim() || value.length < 3) {
      setFilteredUsers([]);
      return;
    }
    
    setSearchingUsers(true);
    
    try {
      const usersRef = collection(db, 'users');
      const q = query(
        usersRef,
        where('email', '>=', value.toLowerCase()),
        where('email', '<=', value.toLowerCase() + '\uf8ff'),
        limit(10)
      );
      
      const snapshot = await getDocs(q);
      const userResults = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setFilteredUsers(userResults);
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setSearchingUsers(false);
    }
  };
  
  // Handle selecting a user for referral partner
  const handleSelectUser = (selectedUser) => {
    setSelectedUser(selectedUser);
    setNewReferralName(selectedUser.fullName || '');
    setNewReferralEmail(selectedUser.email || '');
    setReferralSearchValue(selectedUser.email || '');
    setFilteredUsers([]);
  };

  // Function to filter users specifically for referral partner selection
  const filterUsersForReferral = () => {
    if (!users || !referralSearchValue) return [];
    
    return users.filter(user => {
      const nameMatch = user.name?.toLowerCase().includes(referralSearchValue.toLowerCase());
      const emailMatch = user.email?.toLowerCase().includes(referralSearchValue.toLowerCase());
      return nameMatch || emailMatch;
    });
  };

  // Reset all form fields when opening the create dialog
  const openCreateReferralDialog = () => {
    setSelectedUser(null);
    setNewReferralName('');
    setNewReferralEmail('');
    setNewReferralCommission('10');
    setReferralSearchValue('');
    setManualReferralCode('');
    setUseManualCode(false);
    setCreateReferralDialogOpen(true);
  };

  // Calculate pending commissions for all referral partners
  const calculatePendingCommissions = async () => {
    try {
      setLoading(true);
      
      // Get all referral partners
      const partnersRef = collection(db, 'referralSystem');
      const partnersSnapshot = await getDocs(partnersRef);
      
      // Get all payouts to subtract from pending amounts
      const payoutsRef = collection(db, 'referralPayouts');
      const payoutsSnapshot = await getDocs(payoutsRef);
      
      // Create a map of partner ID to paid amounts
      const paidAmounts = {};
      const paidSubscriptions = {}; // Track which subscriptions have been paid for
      
      payoutsSnapshot.docs.forEach(doc => {
        const payout = doc.data();
        // Consider all payouts that are not failed, regardless of status (pending or completed)
        if (payout.status !== 'failed' && payout.partnerId) {
          // Initialize if not exists
          if (!paidAmounts[payout.partnerId]) {
            paidAmounts[payout.partnerId] = 0;
            paidSubscriptions[payout.partnerId] = new Set();
          }
          
          // Add to total paid amount
          paidAmounts[payout.partnerId] += parseFloat(payout.amount) || 0;
          
          // If the payout includes specific subscriptions, mark them as paid
          if (payout.subscriptions && Array.isArray(payout.subscriptions)) {
            payout.subscriptions.forEach(sub => {
              if (sub.userId) {
                paidSubscriptions[payout.partnerId].add(sub.userId);
              }
            });
          }
        }
      });
      
      console.log('Paid amounts by partner:', paidAmounts);
      console.log('Paid subscriptions by partner:', paidSubscriptions);
      
      const pendingPayoutsData = {};
      const today = new Date();
      
      // For each partner, calculate pending commissions
      for (const partnerDoc of partnersSnapshot.docs) {
        const partner = { id: partnerDoc.id, ...partnerDoc.data() };
        console.log(`Processing partner: ${partner.partnerName || partner.name || 'Unknown'} (${partner.id})`);
        
        // Skip if partner is not active
        if (partner.isActive === false) {
          console.log(`Skipping inactive partner: ${partner.id}`);
          continue;
        }
        
        // Get referred users directly from the referralSystem document
        const referredUsers = partner.referredUsers || [];
        console.log(`Partner has ${referredUsers.length} referred users`);
        
        let totalEarnedAmount = 0;
        let pendingAmount = 0;
        const activeUsers = [];
        
        // Get the set of already paid user IDs for this partner
        const paidUserIds = paidSubscriptions[partner.id] || new Set();
        
        // Calculate commission for each referred user with active subscription
        referredUsers.forEach(user => {
          console.log(`Processing referred user: ${user.email || user.userId || 'Unknown'}`);
          
          if (user.subscription && user.subscription.active) {
            console.log(`User has active subscription: ${JSON.stringify(user.subscription)}`);
            
            // Skip if subscription date doesn't exist or is less than minSubscriptionDays old
            let subscriptionDate = null;
            
            // Try to get subscription date from various possible fields
            if (user.subscription.startDate) {
              subscriptionDate = new Date(user.subscription.startDate);
            } else if (user.subscription.createdAt) {
              subscriptionDate = new Date(user.subscription.createdAt);
            } else if (user.activatedAt) {
              subscriptionDate = new Date(user.activatedAt);
            } else if (user.registeredAt) {
              subscriptionDate = new Date(user.registeredAt);
            } else {
              // If no date is available, use a date 60 days ago to ensure it passes the filter
              const fallbackDate = new Date();
              fallbackDate.setDate(fallbackDate.getDate() - 60);
              subscriptionDate = fallbackDate;
              console.log('Using fallback date for subscription:', fallbackDate);
            }
            
            const diffDays = Math.floor((today - subscriptionDate) / (1000 * 60 * 60 * 24));
            console.log(`Subscription age: ${diffDays} days, Minimum required: ${minSubscriptionDays} days`);
            
            // Skip if subscription is less than minSubscriptionDays old (unless minSubscriptionDays is 0)
            if (minSubscriptionDays > 0 && diffDays < minSubscriptionDays) {
              console.log(`Skipping user: Subscription too new (${diffDays} days < ${minSubscriptionDays} days)`);
              return;
            }
            
            const planPrice = user.subscription.planPrice || 0;
            const userId = user.userId;
            const isPaid = paidUserIds.has(userId);
            
            // Use the stored commission rate if available, otherwise use the current partner rate
            // This ensures that commission rate changes only apply to new subscriptions
            let commissionRate;
            if (user.subscription.commissionRate !== undefined && user.subscription.commissionRate !== null) {
              // Make sure to parse the commission rate as a number
              const storedRate = parseFloat(user.subscription.commissionRate);
              if (!isNaN(storedRate)) {
                commissionRate = storedRate / 100;
              } else {
                commissionRate = partner.commission / 100;
              }
            } else {
              commissionRate = partner.commission / 100;
            }
            
            const userCommission = planPrice * commissionRate;
            totalEarnedAmount += userCommission;
            
            // Only add to pending amount if this subscription hasn't been paid yet
            if (!isPaid) {
              pendingAmount += userCommission;
              console.log(`Added base commission to pending: $${userCommission.toFixed(2)} (${planPrice} * ${commissionRate})`);
            } else {
              console.log(`Subscription already paid, not adding to pending: $${userCommission.toFixed(2)}`);
            }
            
            // Add renewal commissions if they exist
            if (user.renewals && Array.isArray(user.renewals)) {
              console.log(`User has ${user.renewals.length} renewals`);
              
              // Count renewals that occurred after minSubscriptionDays from subscription start
              const eligibleRenewals = user.renewals.filter(renewal => {
                const renewalDate = new Date(renewal.date);
                const daysSinceSubscription = Math.floor((renewalDate - subscriptionDate) / (1000 * 60 * 60 * 24));
                return minSubscriptionDays === 0 || daysSinceSubscription >= minSubscriptionDays;
              });
              
              console.log(`Found ${eligibleRenewals.length} eligible renewals`);
              
              // Add commission for each eligible renewal
              eligibleRenewals.forEach(renewal => {
                const renewalPrice = renewal.planPrice || planPrice;
                
                // Use the stored commission rate for the renewal if available,
                // otherwise use the rate at the time of the renewal or the current partner rate
                let renewalCommissionRate;
                if (renewal.commissionRate !== undefined && renewal.commissionRate !== null) {
                  // Make sure to parse the commission rate as a number
                  const storedRate = parseFloat(renewal.commissionRate);
                  if (!isNaN(storedRate)) {
                    renewalCommissionRate = storedRate / 100;
                  } else {
                    renewalCommissionRate = commissionRate; // Fall back to the subscription rate
                  }
                } else {
                  renewalCommissionRate = commissionRate; // Fall back to the subscription rate
                }
                
                const renewalCommission = renewalPrice * renewalCommissionRate;
                totalEarnedAmount += renewalCommission;
                
                // Only add to pending amount if this renewal hasn't been paid yet
                // We're assuming renewals for paid subscriptions are also paid
                if (!isPaid) {
                  pendingAmount += renewalCommission;
                  console.log(`Added renewal commission to pending: $${renewalCommission.toFixed(2)} (${renewalPrice} * ${renewalCommissionRate})`);
                } else {
                  console.log(`Renewal already paid, not adding to pending: $${renewalCommission.toFixed(2)}`);
                }
              });
            }
            
            activeUsers.push({
              userId: user.userId,
              email: user.email,
              planPrice: planPrice,
              commission: userCommission,
              subscriptionDate: subscriptionDate,
              subscriptionAge: diffDays,
              renewals: user.renewals || [],
              isPaid: isPaid
            });
          } else {
            console.log('Skipping user: No active subscription');
          }
        });
        
        // Calculate the actual pending amount by subtracting all paid amounts
        const totalPaid = paidAmounts[partner.id] || 0;
        pendingAmount = Math.max(0, totalEarnedAmount - totalPaid);
        
        console.log(`Partner ${partner.id}: Total earned: $${totalEarnedAmount.toFixed(2)}, Total paid: $${totalPaid.toFixed(2)}, Pending: $${pendingAmount.toFixed(2)}`);
        
        // Only add partners with pending amounts greater than 0
        if (pendingAmount > 0) {
          console.log(`Adding partner ${partner.id} to pending payouts with $${pendingAmount.toFixed(2)}`);
          pendingPayoutsData[partner.id] = {
            partnerId: partner.id,
            partnerName: partner.partnerName || 'Unknown',
            partnerEmail: partner.partnerEmail || 'Unknown',
            paypalEmail: partner.paypalEmail || 'Not set',
            pendingAmount: pendingAmount,
            totalEarned: totalEarnedAmount,
            totalPaid: totalPaid,
            referredUsers: activeUsers,
            commissionRate: partner.commission || 0
          };
        } else {
          console.log(`Skipping partner ${partner.id}: No pending amount`);
        }
      }
      
      console.log(`Final pending payouts data: ${Object.keys(pendingPayoutsData).length} partners`);
      setPendingPayouts(pendingPayoutsData);
      return pendingPayoutsData;
    } catch (error) {
      console.error('Error calculating pending commissions:', error);
      return {};
    } finally {
      setLoading(false);
    }
  };

  const fetchPayoutHistory = async () => {
    try {
      setLoading(true);
      
      // Fetch all payout records
      const payoutsRef = collection(db, 'referralPayouts');
      const payoutsSnapshot = await getDocs(payoutsRef);
      
      const payoutHistoryData = payoutsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by date, newest first
      payoutHistoryData.sort((a, b) => {
        return new Date(b.date) - new Date(a.date);
      });
      
      setPayoutHistory(payoutHistoryData);
    } catch (error) {
      console.error('Error fetching payout history:', error);
      alert('Failed to fetch payout history: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch referral settings
  const fetchReferralSettings = async () => {
    try {
      const settingsRef = doc(db, 'system_settings', 'referral_settings');
      const settingsDoc = await getDoc(settingsRef);
      
      if (settingsDoc.exists()) {
        // Make sure we're getting a valid number
        const bonusCredits = parseInt(settingsDoc.data().bonusCredits);
        setReferralBonusSettings(isNaN(bonusCredits) ? 500 : bonusCredits);
      } else {
        // Create default settings if they don't exist
        await setDoc(settingsRef, { bonusCredits: 500 });
        setReferralBonusSettings(500);
      }
    } catch (error) {
      console.error('Error fetching referral settings:', error);
      setReferralBonusSettings(500); // Default to 500 on error
    }
  };
  
  // Fetch trial plan settings
  const fetchTrialPlanSettings = async () => {
    try {
      setLoadingTrialPlan(true);
      const settingsRef = doc(db, 'trial_plan_settings', 'settings');
      const settingsDoc = await getDoc(settingsRef);
      
      if (settingsDoc.exists()) {
        const settings = settingsDoc.data();
        setTrialPlanSettings({
          credits: settings.credits || 20,
          duration_days: settings.duration_days || 1,
          stripe_product_id: settings.stripe_product_id || '',
          stripe_price_id: settings.stripe_price_id || ''
        });
      } else {
        // Initialize with defaults if not exists
        await setDoc(settingsRef, {
          credits: 20,
          duration_days: 1,
          stripe_product_id: '',
          stripe_price_id: '',
          updated_at: new Date().toISOString()
        });
        setTrialPlanSettings({
          credits: 20,
          duration_days: 1,
          stripe_product_id: '',
          stripe_price_id: ''
        });
      }
    } catch (error) {
      console.error('Error fetching trial plan settings:', error);
      setError('Failed to load trial plan settings');
    } finally {
      setLoadingTrialPlan(false);
    }
  };

  // Update trial plan settings
  const updateTrialPlanSettings = async () => {
    try {
      // Validate the inputs
      const credits = parseInt(trialPlanSettings.credits);
      const duration = parseInt(trialPlanSettings.duration_days);
      
      if (isNaN(credits) || credits < 1) {
        setError('Credits must be a positive number');
        return;
      }
      
      if (isNaN(duration) || duration < 1) {
        setError('Duration days must be a positive number');
        return;
      }
      
      setLoadingTrialPlan(true);
      const settingsRef = doc(db, 'trial_plan_settings', 'settings');
      await setDoc(settingsRef, {
        credits: credits,
        duration_days: duration,
        stripe_product_id: trialPlanSettings.stripe_product_id,
        stripe_price_id: trialPlanSettings.stripe_price_id,
        updated_at: new Date().toISOString()
      }, { merge: true });
      
      setSuccess('Trial plan settings updated successfully');
      setTimeout(() => setSuccess(''), 3000);
      setShowTrialPlanModal(false);
    } catch (error) {
      console.error('Error updating trial plan settings:', error);
      setError('Failed to update trial plan settings: ' + error.message);
    } finally {
      setLoadingTrialPlan(false);
    }
  };
  
  // Render the trial plan settings panel
  const renderTrialPlanPanel = () => {
    return (
      <Card>
        <Card.Section>
          <Banner status="info">
            <p>Configure trial plan settings here. New users will receive the specified number of credits for the trial period.</p>
          </Banner>
        </Card.Section>
        
        <Card.Section>
          <Stack distribution="equalSpacing" alignment="center">
            <TextStyle variation="strong">Trial Plan Settings</TextStyle>
            <Button 
              primary
              onClick={() => setShowTrialPlanModal(true)}
              loading={loadingTrialPlan}
            >
              Edit Trial Plan Settings
            </Button>
          </Stack>
        </Card.Section>
        
        <Card.Section title="Current Settings">
          <Stack vertical spacing="loose">
            <Stack distribution="equalSpacing">
              <TextStyle>Trial Credits:</TextStyle>
              <TextStyle variation="strong">{trialPlanSettings.credits}</TextStyle>
            </Stack>
            <Stack distribution="equalSpacing">
              <TextStyle>Trial Duration (days):</TextStyle>
              <TextStyle variation="strong">{trialPlanSettings.duration_days}</TextStyle>
            </Stack>
            <Stack distribution="equalSpacing">
              <TextStyle>Stripe Product ID:</TextStyle>
              <TextStyle variation="strong">{trialPlanSettings.stripe_product_id || 'Not set'}</TextStyle>
            </Stack>
            <Stack distribution="equalSpacing">
              <TextStyle>Stripe Price ID:</TextStyle>
              <TextStyle variation="strong">{trialPlanSettings.stripe_price_id || 'Not set'}</TextStyle>
            </Stack>
          </Stack>
        </Card.Section>
      </Card>
    );
  };
  
  // Update referral bonus settings
  const updateReferralBonusSettings = async () => {
    try {
      // Validate the input
      const bonusAmount = parseInt(referralBonusSettings);
      if (isNaN(bonusAmount) || bonusAmount < 0) {
        setError('Please enter a valid number of credits');
        return;
      }
      
      setLoading(true);
      const settingsRef = doc(db, 'system_settings', 'referral_settings');
      await setDoc(settingsRef, { 
        bonusCredits: bonusAmount,
        updatedAt: new Date().toISOString()
      });
      
      setReferralBonusSettings(bonusAmount);
      setSuccess('Referral bonus settings updated successfully');
      setTimeout(() => setSuccess(''), 3000);
      setShowReferralBonusModal(false);
    } catch (error) {
      console.error('Error updating referral bonus settings:', error);
      setError('Failed to update referral bonus settings');
    } finally {
      setLoading(false);
    }
  };

  const renderReferralPayouts = () => {
    return (
      <Card>
        <Card.Section>
          <Banner status="info">
            <p>Manage referral partner payouts here. Calculate commissions and process monthly payments.</p>
          </Banner>
        </Card.Section>
        
        <Card.Section>
          <Stack distribution="equalSpacing" alignment="center">
            <TextStyle variation="strong">Referral Payouts</TextStyle>
            <Button onClick={calculatePendingCommissions} loading={loading}>Calculate Pending Commissions</Button>
            <Button onClick={() => {
              // Set minSubscriptionDays to 0 to show all subscriptions
              setMinSubscriptionDays(0);
              // Force recalculation
              calculatePendingCommissions();
            }}>Debug: Show All</Button>
            <Button onClick={() => fixMissingSubscriptionDates()}>Fix Missing Dates</Button>
            <Select
              label="Minimum Subscription Age"
              labelInline
              options={[
                {label: 'All (No filter)', value: '0'},
                {label: '30 Days', value: '30'},
                {label: '60 Days', value: '60'},
                {label: '90 Days', value: '90'}
              ]}
              value={minSubscriptionDays.toString()}
              onChange={(value) => setMinSubscriptionDays(parseInt(value))}
            />
          </Stack>
        </Card.Section>
        <Card.Section title="Pending Commissions">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <Spinner size="large" />
            </div>
          ) : Object.keys(pendingPayouts).length > 0 ? (
            <DataTable
              columnContentTypes={[
                'text',
                'text',
                'text',
                'numeric',
                'text',
                'text',
                'text'
              ]}
              headings={[
                'Partner',
                'Email',
                'PayPal Email',
                'Commission Rate',
                'Pending Amount',
                'Actions',
                'Edit'
              ]}
              rows={Object.values(pendingPayouts)
                .filter(partner => partner.referredUsers && Array.isArray(partner.referredUsers) && partner.referredUsers.length > 0)
                .map(partner => {
                  // Add safety checks for undefined values
                  const pendingAmount = partner.pendingAmount || 0;
                  const commissionRate = partner.commissionRate || 0;
                  
                  return [
                    partner.partnerName || 'Unknown',
                    partner.partnerEmail || 'Unknown',
                    partner.paypalEmail || 'Not set',
                    `${commissionRate}%`,
                    `$${pendingAmount.toFixed(2)}`,
                    <Button 
                      size="slim" 
                      onClick={() => {
                        setSelectedPartnerForPayout(partner);
                        setPayoutAmount(pendingAmount.toFixed(2));
                        setShowPayoutModal(true);
                      }}
                    >
                      Process Payout
                    </Button>,
                    <Button 
                      size="slim" 
                      onClick={() => {
                        setEditingPayout(partner);
                        setPayoutAmount(pendingAmount.toFixed(2));
                        setShowPayoutModal(true);
                      }}
                    >
                      Edit
                    </Button>
                  ];
                })}
            />
          ) : (
            <EmptyState
              heading="No pending commissions"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>Calculate pending commissions to see referral partners with earnings.</p>
            </EmptyState>
          )}
        </Card.Section>
        <Card.Section title="Recent Payouts">
          <Button onClick={fetchPayoutHistory} loading={loading}>Fetch Payout History</Button>
          
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <Spinner size="large" />
            </div>
          ) : payoutHistory.length > 0 ? (
            <DataTable
              columnContentTypes={[
                'text',
                'text',
                'text',
                'numeric',
                'text',
                'text',
                'text'
              ]}
              headings={[
                'Date',
                'Partner',
                'PayPal Email',
                'Amount',
                'Status',
                'Transaction ID',
                'Actions'
              ]}
              rows={payoutHistory.map(payout => [
                new Date(payout.date).toLocaleDateString(),
                payout.partnerName,
                payout.paypalEmail || 'Not set',
                `$${payout.amount.toFixed(2)}`,
                <Badge status={payout.status === 'completed' ? 'success' : payout.status === 'pending' ? 'attention' : 'critical'}>
                  {payout.status.charAt(0).toUpperCase() + payout.status.slice(1)}
                </Badge>,
                payout.transactionId || '-',
                payout.status !== 'completed' ? (
                  <Button size="slim" onClick={() => handleEditPayout(payout)}>Edit</Button>
                ) : (
                  '-'
                )
              ])}
            />
          ) : (
            <EmptyState
              heading="No payout history"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>Process payouts to see history here.</p>
            </EmptyState>
          )}
        </Card.Section>
      </Card>
    );
  };

  const handleProcessPayout = async () => {
    try {
      setLoading(true);
      
      if (!selectedPartnerForPayout && !editingPayout) {
        throw new Error('No partner selected for payout');
      }
      
      if (!payoutAmount || parseFloat(payoutAmount) <= 0) {
        throw new Error('Please enter a valid payout amount');
      }
      
      if (editingPayout) {
        // Update existing payout
        const payoutRef = doc(db, 'referralPayouts', editingPayout.id);
        await updateDoc(payoutRef, {
          amount: parseFloat(payoutAmount),
          status: payoutStatus,
          transactionId: payoutTransactionId || null,
          updatedAt: new Date().toISOString()
        });
        
        console.log('Updated existing payout:', editingPayout.id);
      } else {
        // Create a new payout record
        const payoutsRef = collection(db, 'referralPayouts');
        const payoutData = {
          partnerId: selectedPartnerForPayout.partnerId || selectedPartnerForPayout.id,
          partnerName: selectedPartnerForPayout.partnerName || 'Unknown',
          partnerEmail: selectedPartnerForPayout.partnerEmail || 'Unknown',
          paypalEmail: selectedPartnerForPayout.paypalEmail || 'Not set',
          amount: parseFloat(payoutAmount),
          date: new Date().toISOString(),
          status: payoutStatus,
          transactionId: payoutTransactionId || null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        await addDoc(payoutsRef, payoutData);
        console.log('Created new payout for partner:', selectedPartnerForPayout.partnerName);
      }
      
      // Reset form and close modal
      setShowPayoutModal(false);
      setSelectedPartnerForPayout(null);
      setEditingPayout(null);
      setPayoutAmount('');
      setPayoutTransactionId('');
      setPayoutStatus('completed');
      
      // Refresh payout history
      await fetchPayoutHistory();
      
      // Recalculate pending commissions
      await calculatePendingCommissions();
      
      // Show success message
      alert(editingPayout ? 'Payout updated successfully!' : 'Payout processed successfully!');
    } catch (error) {
      console.error('Error processing payout:', error);
      alert(`Failed to ${editingPayout ? 'update' : 'process'} payout: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPayout = (payout) => {
    // Set the payout data in the state for editing
    setEditingPayout(payout);
    setPayoutAmount(payout.amount.toString());
    setPayoutTransactionId(payout.transactionId || '');
    setPayoutStatus(payout.status || 'pending');
    setShowPayoutModal(true);
  };

  // Function to fix missing startDates in referral system
  const fixMissingSubscriptionDates = async () => {
    try {
      setLoading(true);
      console.log('Starting to fix missing subscription dates...');
      
      // Get all referral partners
      const partnersSnapshot = await getDocs(collection(db, 'referralSystem'));
      
      let updatedCount = 0;
      
      // Loop through each partner
      for (const partnerDoc of partnersSnapshot.docs) {
        const partner = { id: partnerDoc.id, ...partnerDoc.data() };
        const referredUsers = partner.referredUsers || [];
        let needsUpdate = false;
        
        // Check each referred user for missing subscription dates
        referredUsers.forEach((user, index) => {
          if (user.subscription && user.subscription.active) {
            if (!user.subscription.startDate) {
              console.log(`Fixing missing startDate for user: ${user.email || user.userId || 'Unknown'}`);
              
              // Set startDate based on available data or current date
              if (user.activatedAt) {
                referredUsers[index].subscription.startDate = user.activatedAt;
                console.log(`Using activatedAt date: ${user.activatedAt}`);
              } else if (user.registeredAt) {
                referredUsers[index].subscription.startDate = user.registeredAt;
                console.log(`Using registeredAt date: ${user.registeredAt}`);
              } else {
                // Use 60 days ago as a reasonable default for existing subscriptions
                const startDate = new Date();
                startDate.setDate(startDate.getDate() - 60);
                referredUsers[index].subscription.startDate = startDate.toISOString();
                console.log(`Using default date (60 days ago): ${startDate.toISOString()}`);
              }
              
              needsUpdate = true;
              updatedCount++;
            }
          }
        });
        
        // Update the document if any users were fixed
        if (needsUpdate) {
          console.log(`Updating referral document for partner ${partner.id} with fixed dates`);
          await updateDoc(doc(db, 'referralSystem', partner.id), {
            referredUsers: referredUsers,
            updatedAt: new Date().toISOString()
          });
        }
      }
      
      console.log(`Fixed ${updatedCount} missing subscription dates`);
      alert(`Fixed ${updatedCount} missing subscription dates. Please calculate pending commissions again.`);
      
      // Recalculate pending commissions
      await calculatePendingCommissions();
      
      return updatedCount;
    } catch (error) {
      console.error('Error fixing missing subscription dates:', error);
      alert('Error fixing missing subscription dates: ' + error.message);
      return 0;
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateToolDescriptions = async () => {
    try {
      // Get the current tool descriptions from the form or state
      const priceToolsDescription = document.getElementById('priceToolsDescription')?.value || '';
      const inventoryToolsDescription = document.getElementById('inventoryToolsDescription')?.value || '';
      const productToolsDescription = document.getElementById('productToolsDescription')?.value || '';
      
      // Update the tool descriptions in Firestore
      const toolDescriptionsRef = doc(db, 'settings', 'toolDescriptions');
      await setDoc(toolDescriptionsRef, {
        priceTools: priceToolsDescription,
        inventoryTools: inventoryToolsDescription,
        productTools: productToolsDescription,
        updatedAt: new Date().toISOString()
      });
      
      alert('Tool descriptions updated successfully');
    } catch (error) {
      console.error('Error updating tool descriptions:', error);
      alert('Failed to update tool descriptions: ' + error.message);
    }
  };



  return (
    <Page title="Admin Panel">
      <Tabs
        tabs={tabs}
        selected={selectedTab}
        onSelect={setSelectedTab}
      />
      
      <Layout>
        <Layout.Section>
          {selectedTab === 0 && (
            renderUserManagement()
          )}

          {selectedTab === 1 && (
            <>
              <Card sectioned>
                <Banner status="info">
                  <p>Credits are now managed through subscriptions. Monthly credits are reset when a subscription renews. Flex credits don't expire.</p>
                </Banner>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
                  <div style={{ width: '60%' }}>
                    <TextField
                      label="Search users"
                      value={searchCreditValue}
                      onChange={setSearchCreditValue}
                      placeholder="Search by email or name"
                      clearButton
                      onClearButtonClick={() => setSearchCreditValue('')}
                      connectedRight={<Button onClick={() => setCreditCurrentPage(1)}>Search</Button>}
                    />
                  </div>
                  <div>
                    <Button
                      primary
                      onClick={() => setAddingFlexCreditsToAll(true)}
                    >
                      Add Flex Credits to All Users
                    </Button>
                  </div>
                </div>
              </Card>
              <Card>
                <DataTable
                  columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'text', 'text']}
                  headings={['Email', 'Monthly Credits', 'Flex Credits', 'Used Credits', 'Subscription', 'Actions']}
                  rows={creditRows}
                />
              </Card>
              <Card sectioned>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    hasPrevious={creditCurrentPage > 1}
                    onPrevious={() => setCreditCurrentPage(creditCurrentPage - 1)}
                    hasNext={creditCurrentPage * creditsPerPage < filteredCreditUsers.length}
                    onNext={() => setCreditCurrentPage(creditCurrentPage + 1)}
                  />
                </div>
                <div style={{ textAlign: 'center', marginTop: '10px' }}>
                  <TextStyle variation="subdued">
                    Showing {Math.min((creditCurrentPage - 1) * creditsPerPage + 1, filteredCreditUsers.length)} - {Math.min(creditCurrentPage * creditsPerPage, filteredCreditUsers.length)} of {filteredCreditUsers.length} users
                  </TextStyle>
                </div>
              </Card>
            </>
          )}

          {selectedTab === 2 && (
            <Card>
              <Card.Section>
                <Banner status="info">
                  <p>Configure subscription plans here. Users will receive the specified number of credits each month when subscribed to a plan.</p>
                </Banner>
              </Card.Section>
              <DataTable
                columnContentTypes={['text', 'text', 'numeric', 'text', 'text']}
                headings={['Plan Name', 'Price', 'Monthly Credits', 'Status', 'Actions']}
                rows={subscriptionPlanRows}
              />
            </Card>
          )}

          {selectedTab === 3 && (
            <Card>
              <Card.Section>
                <Banner status="info">
                  <p>Configure flex credit packages here. Users can purchase these packages to add flex credits to their account.</p>
                </Banner>
              </Card.Section>
              <DataTable
                columnContentTypes={['text', 'text', 'numeric', 'text', 'text']}
                headings={['Package Name', 'Price', 'Credits', 'Status', 'Actions']}
                rows={flexCreditPackageRows}
              />
            </Card>
          )}

          {selectedTab === 4 && (
            <Card>
              <Card.Section>
                <Banner status="info">
                  <p>Configure Stripe settings here.</p>
                </Banner>
              </Card.Section>
              <ButtonGroup>
                <Button
                  primary
                  onClick={handleEditStripeSettings}
                >
                  Edit Stripe Settings
                </Button>
              </ButtonGroup>
            </Card>
          )}

          {selectedTab === 5 && (
            <ServerStatusPage />
          )}

          {selectedTab === 6 && (
            <SystemSettingsPage />
          )}

          {selectedTab === 7 && (
            renderLogs()
          )}

          {selectedTab === 8 && (
            <Card>
              <Card.Section>
                <Banner status="info">
                  <p>Manage sales here. You can apply sales to subscription plans and credit packages.</p>
                </Banner>
              </Card.Section>
              
              <Card.Section>
                <Stack distribution="equalSpacing" alignment="center">
                  <TextStyle variation="strong">Sales Management</TextStyle>
                  <Button
                    primary
                    onClick={() => {
                      setSaleName('');
                      setSubscriptionSalePercentage(0);
                      setCreditsSalePercentage(0);
                      setSalesDialogOpen(true);
                    }}
                  >
                    Create New Sale
                  </Button>
                </Stack>
              </Card.Section>
              
              <Card.Section title="Subscription Plans Sale">
                {activeSales.subscription.active ? (
                  <Banner status="success">
                    <Stack distribution="equalSpacing" alignment="center">
                      <Stack vertical spacing="tight">
                        <TextStyle variation="strong">{activeSales.subscription.name}</TextStyle>
                        <p>{activeSales.subscription.percentage}% off all subscription plans</p>
                      </Stack>
                      <ButtonGroup>
                        <Button 
                          onClick={() => {
                            setSaleName(activeSales.subscription.name);
                            setSubscriptionSalePercentage(activeSales.subscription.percentage);
                            setCreditsSalePercentage(0);
                            setSalesDialogOpen(true);
                          }}
                        >
                          Edit
                        </Button>
                        <Button destructive onClick={removeSubscriptionSale}>Remove Sale</Button>
                      </ButtonGroup>
                    </Stack>
                  </Banner>
                ) : (
                  <Banner status="info">
                    <p>No active subscription sale</p>
                  </Banner>
                )}
              </Card.Section>
              
              <Card.Section title="Flex Credits Sale">
                {activeSales.credits.active ? (
                  <Banner status="success">
                    <Stack distribution="equalSpacing" alignment="center">
                      <Stack vertical spacing="tight">
                        <TextStyle variation="strong">{activeSales.credits.name}</TextStyle>
                        <p>{activeSales.credits.percentage}% off all flex credit packages</p>
                      </Stack>
                      <ButtonGroup>
                        <Button 
                          onClick={() => {
                            setSaleName(activeSales.credits.name);
                            setSubscriptionSalePercentage(0);
                            setCreditsSalePercentage(activeSales.credits.percentage);
                            setSalesDialogOpen(true);
                          }}
                        >
                          Edit
                        </Button>
                        <Button destructive onClick={removeCreditsSale}>Remove Sale</Button>
                      </ButtonGroup>
                    </Stack>
                  </Banner>
                ) : (
                  <Banner status="info">
                    <p>No active credits sale</p>
                  </Banner>
                )}
              </Card.Section>
            </Card>
          )}

          {selectedTab === 9 && (
            renderMaintenanceManagement()
          )}

          {selectedTab === 10 && (
            <BetaManagementPage />
          )}

          {selectedTab === 11 && (
            <Navigate to="/app/tracking-admin" />
          )}

          {selectedTab === 12 && (
            renderReferralManagement()
          )}

          {selectedTab === 13 && (
            renderReferralPayouts()
          )}
          
          {selectedTab === 14 && (
            <AIModelManagement />
          )}
          
          {selectedTab === 15 && (
            renderTrialPlanPanel()
          )}
        </Layout.Section>
      </Layout>

      {/* Edit Credits Modal */}
      <Modal
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        title="Edit User Credits"
        primaryAction={{
          content: 'Save',
          onAction: handleSaveEdit,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setEditDialogOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <TextField
            label="Total Credits"
            type="number"
            value={editCredits}
            onChange={setEditCredits}
            autoComplete="off"
            helpText="This will update both total and monthly credits for compatibility"
          />
        </Modal.Section>
      </Modal>

      {/* Add Flex Credits to All Modal */}
      <Modal
        open={addingFlexCreditsToAll}
        onClose={() => setAddingFlexCreditsToAll(false)}
        title="Add Flex Credits to All Users"
        primaryAction={{
          content: 'Add Credits',
          onAction: handleAddFlexCreditsToAll,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setAddingFlexCreditsToAll(false),
          },
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <p>Enter the number of flex credits to add to all users:</p>
            <TextField
              label="Flex Credits to Add"
              type="number"
              value={flexCreditsToAdd}
              onChange={setFlexCreditsToAdd}
              autoComplete="off"
            />
          </TextContainer>
        </Modal.Section>
      </Modal>

      {/* Edit Subscription Plan Modal */}
      <Modal
        open={editPlanDialogOpen}
        onClose={() => setEditPlanDialogOpen(false)}
        title={`Edit ${editPlanName} Plan`}
        primaryAction={{
          content: 'Save Plan',
          onAction: handleSavePlan,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setEditPlanDialogOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Plan Name"
              value={editPlanName}
              onChange={setEditPlanName}
              autoComplete="off"
            />
            <TextField
              label="Monthly Credits"
              type="number"
              value={editPlanCredits}
              onChange={setEditPlanCredits}
              autoComplete="off"
            />
            <TextField
              label="Price"
              type="number"
              value={editPlanPrice}
              onChange={setEditPlanPrice}
              autoComplete="off"
              prefix="$"
            />
            <TextField
              label="Max Product Selection"
              type="number"
              value={editPlanMaxProductSelection}
              onChange={setEditPlanMaxProductSelection}
              autoComplete="off"
              helpText="Maximum number of products that can be selected at once"
            />
            <TextField
              label="Stripe Product ID (Monthly)"
              value={editPlanStripeProductId}
              onChange={setEditPlanStripeProductId}
              autoComplete="off"
              helpText="Product ID from Stripe dashboard for monthly billing"
            />
            <TextField
              label="Stripe Price ID (Monthly)"
              value={editPlanStripePriceId}
              onChange={setEditPlanStripePriceId}
              autoComplete="off"
              helpText="Price ID from Stripe dashboard for monthly billing"
            />
            <TextField
              label="Stripe Product ID (Yearly)"
              value={editPlanYearlyProductId || ''}
              onChange={(value) => setEditPlanYearlyProductId(value)}
              autoComplete="off"
              helpText="Product ID from Stripe dashboard for yearly billing"
            />
            <TextField
              label="Stripe Price ID (Yearly)"
              value={editPlanYearlyPriceId || ''}
              onChange={(value) => setEditPlanYearlyPriceId(value)}
              autoComplete="off"
              helpText="Price ID from Stripe dashboard for yearly billing"
            />
            <TextField
              label="Yearly Discount Percentage"
              type="number"
              value={editPlanYearlyDiscountPercentage || '20'}
              onChange={(value) => setEditPlanYearlyDiscountPercentage(value)}
              autoComplete="off"
              suffix="%"
              helpText="Discount percentage applied to yearly plans (e.g., 20% for 2 months free)"
            />
            <Checkbox
              label="Enabled"
              checked={editPlanEnabled}
              onChange={setEditPlanEnabled}
            />
            
            <TextContainer>
              <p>Plan Features (up to 5)</p>
              <Stack vertical spacing="tight">
                {editPlanFeatures.map((feature, index) => (
                  <Stack alignment="center" key={index}>
                    <Stack.Item fill>
                      <TextField
                        value={feature}
                        onChange={(value) => {
                          const newFeatures = [...editPlanFeatures];
                          newFeatures[index] = value;
                          setEditPlanFeatures(newFeatures);
                        }}
                      />
                    </Stack.Item>
                    <Button
                      plain
                      destructive
                      onClick={() => {
                        const newFeatures = [...editPlanFeatures];
                        newFeatures.splice(index, 1);
                        setEditPlanFeatures(newFeatures);
                      }}
                    >
                      Remove
                    </Button>
                  </Stack>
                ))}
                
                {editPlanFeatures.length < 5 && (
                  <Stack alignment="center">
                    <Stack.Item fill>
                      <TextField
                        value={newFeature}
                        onChange={setNewFeature}
                        placeholder="Add a new feature"
                      />
                    </Stack.Item>
                    <Button
                      plain
                      onClick={() => {
                        if (newFeature.trim()) {
                          setEditPlanFeatures([...editPlanFeatures, newFeature.trim()]);
                          setNewFeature('');
                        }
                      }}
                    >
                      Add
                    </Button>
                  </Stack>
                )}
              </Stack>
            </TextContainer>
            <TextField
              label="Product Count Text"
              value={editPlanProductCount}
              onChange={setEditPlanProductCount}
              autoComplete="off"
              helpText="Customizable text describing the product count for this plan (e.g., 'Up to 5 products', 'Unlimited products', etc.)"
            />
            <div>
              <Text variant="bodyMd" as="p" fontWeight="medium">Product Count Explanation</Text>
              <div style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                <SummernoteEditor 
                  value={editPlanProductCountExplanation} 
                  onChange={setEditPlanProductCountExplanation}
                  height={200}
                />
              </div>
              <Text variant="bodyMd" as="p" color="subdued">
                HTML editor for rich formatting. This explanation appears when users click the info icon next to the product count.
              </Text>
            </div>
            <TextField
              label="Plan Description"
              value={editPlanDescription}
              onChange={setEditPlanDescription}
              autoComplete="off"
              helpText="Short description of the plan"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Assign Subscription Modal */}
      <Modal
        open={assignSubscriptionDialogOpen}
        onClose={() => setAssignSubscriptionDialogOpen(false)}
        title={`Set Subscription for ${editUser?.email || ''}`}
        primaryAction={{
          content: 'Save Subscription',
          onAction: handleSaveSubscription,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setAssignSubscriptionDialogOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <Select
              label="Subscription Plan"
              options={Object.entries(subscriptionPlans).map(([key, plan]) => ({
                label: `${plan.name} ($${plan.price}/mo - ${plan.monthlyCredits} credits)`,
                value: key
              }))}
              value={selectedSubscriptionType}
              onChange={setSelectedSubscriptionType}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Add Flex Credits Modal */}
      <Modal
        open={addFlexCreditsDialogOpen}
        onClose={() => setAddFlexCreditsDialogOpen(false)}
        title={`Edit Flex Credits for ${editUser?.email || ''}`}
        primaryAction={{
          content: 'Save Changes',
          onAction: handleSaveFlexCredits,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setAddFlexCreditsDialogOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Flex Credits"
              type="number"
              value={flexCreditsToAdd}
              onChange={setFlexCreditsToAdd}
              autoComplete="off"
              helpText="Set the total number of flex credits for this user. These credits don't expire and are used when monthly credits are depleted."
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Edit Flex Credit Package Modal */}
      <Modal
        open={editPackageDialogOpen}
        onClose={() => setEditPackageDialogOpen(false)}
        title={`Edit ${editPackageName} Package`}
        primaryAction={{
          content: 'Save Package',
          onAction: handleSavePackage,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setEditPackageDialogOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Package Name"
              value={editPackageName}
              onChange={setEditPackageName}
              autoComplete="off"
            />
            <TextField
              label="Credits"
              type="number"
              value={editPackageCredits}
              onChange={setEditPackageCredits}
              autoComplete="off"
              helpText="Number of credits users receive when purchasing this package"
            />
            <TextField
              label="Price (USD)"
              type="number"
              value={editPackagePrice}
              onChange={setEditPackagePrice}
              autoComplete="off"
              prefix="$"
            />
            <TextField
              label="Stripe Product ID"
              value={editPackageStripeProductId}
              onChange={setEditPackageStripeProductId}
              autoComplete="off"
            />
            <TextField
              label="Stripe Price ID"
              value={editPackageStripePriceId}
              onChange={setEditPackageStripePriceId}
              autoComplete="off"
            />
            <Checkbox
              label="Enabled"
              checked={editPackageEnabled}
              onChange={setEditPackageEnabled}
            />
            <TextField
              label="Package Description"
              value={editPackageDescription}
              onChange={setEditPackageDescription}
              autoComplete="off"
              helpText="Short description of the package"
            />
            <div>
              <Text variant="bodyMd" as="p" fontWeight="medium">Package Credit Count Text</Text>
              <div style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                <TextField
                  label="Credit Count Text"
                  value={editPackageCreditCountText}
                  onChange={setEditPackageCreditCountText}
                  autoComplete="off"
                  helpText="Customizable text describing the credit count for this package (e.g., 'Up to 5 credits', 'Unlimited credits', etc.)"
                />
              </div>
              <Text variant="bodyMd" as="p" fontWeight="medium">Package Credit Count Explanation</Text>
              <div style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                <SummernoteEditor 
                  value={editPackageCreditCountExplanation} 
                  onChange={setEditPackageCreditCountExplanation}
                  height={200}
                />
              </div>
              <Text variant="bodyMd" as="p" color="subdued">
                HTML editor for rich formatting. This explanation appears when users click the info icon next to the package.
              </Text>
            </div>
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Purchase Flex Credits Modal */}
      <Modal
        open={purchasePackageDialogOpen}
        onClose={() => setPurchasePackageDialogOpen(false)}
        title={`Purchase Flex Credits for ${editUser?.email || ''}`}
        primaryAction={{
          content: 'Purchase',
          onAction: handleSavePurchase,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setPurchasePackageDialogOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <Select
              label="Flex Credit Package"
              options={Object.entries(flexCreditPackages).map(([key, packageData]) => ({
                label: `${packageData.name} ($${packageData.price} - ${packageData.credits} credits)`,
                value: key
              }))}
              value={selectedPackageType}
              onChange={setSelectedPackageType}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Edit Stripe Settings Modal */}
      <Modal
        open={editStripeSettingsDialogOpen}
        onClose={() => setEditStripeSettingsDialogOpen(false)}
        title="Edit Stripe Settings"
        primaryAction={{
          content: 'Save Settings',
          onAction: handleSaveStripeSettings,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setEditStripeSettingsDialogOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Secret Key (API Key)"
              value={editStripeApiKey}
              onChange={setEditStripeApiKey}
              autoComplete="off"
              helpText="Stripe Secret Key used for server-side operations. Never expose this to clients."
            />
            <TextField
              label="Publishable Key"
              value={editStripePublishableKey}
              onChange={setEditStripePublishableKey}
              autoComplete="off"
              helpText="Stripe Publishable Key that can be safely used in client-side code."
            />
            <TextField
              label="Webhook Secret"
              value={editStripeWebhookSecret}
              onChange={setEditStripeWebhookSecret}
              autoComplete="off"
              helpText="Used to verify webhook events are coming from Stripe."
            />
            <Checkbox
              label="Test Mode"
              checked={editStripeTestMode}
              onChange={setEditStripeTestMode}
              helpText="When enabled, uses Stripe test environment instead of live."
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* VIP Status Modal */}
      <Modal
        open={vipDialogOpen}
        onClose={() => setVipDialogOpen(false)}
        title={`Set VIP Status for ${vipUserId}`}
        primaryAction={{
          content: 'Set VIP Status',
          onAction: handleSetVIPStatus
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setVipDialogOpen(false)
          }
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <Select
              label="VIP Plan"
              options={Object.entries(subscriptionPlans).map(([key, plan]) => ({
                label: `${plan.name} ($${plan.price}/mo - ${plan.monthlyCredits} credits)`,
                value: key
              }))}
              value={vipPlanType}
              onChange={setVipPlanType}
            />
            
            <Select
              label="Duration Type"
              options={[
                {label: 'Unlimited', value: 'unlimited'},
                {label: 'Limited Months', value: 'limited'}
              ]}
              value={vipDurationType}
              onChange={setVipDurationType}
            />
            
            {vipDurationType === 'limited' && (
              <TextField
                label="Number of Months"
                type="number"
                value={vipMonths.toString()}
                onChange={(value) => setVipMonths(parseInt(value) || 1)}
                min={1}
                autoComplete="off"
              />
            )}
            
            <Banner status="info">
              <p>VIP status gives the user access to all features of the selected plan without requiring a regular subscription. {vipDurationType === 'limited' ? `This VIP status will automatically expire after ${vipMonths} month${vipMonths === 1 ? '' : 's'}.` : 'This VIP status will never expire unless manually removed.'}</p>
            </Banner>
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Sales Management Modal */}
      <Modal
        open={salesDialogOpen}
        onClose={() => setSalesDialogOpen(false)}
        title="Create Sale"
        primaryAction={{
          content: 'Apply Sale',
          onAction: () => {
            if (subscriptionSalePercentage > 0) {
              applySubscriptionSale();
            } else if (creditsSalePercentage > 0) {
              applyCreditsSale();
            } else {
              alert('Please enter a valid sale percentage');
            }
          },
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setSalesDialogOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Sale Name"
              value={saleName}
              onChange={setSaleName}
              autoComplete="off"
            />
            <TextField
              label="Subscription Sale Percentage"
              type="number"
              value={subscriptionSalePercentage}
              onChange={(value) => setSubscriptionSalePercentage(parseFloat(value))}
              autoComplete="off"
              helpText="Enter a percentage value (e.g. 10 for 10% off)"
            />
            <TextField
              label="Credits Sale Percentage"
              type="number"
              value={creditsSalePercentage}
              onChange={(value) => setCreditsSalePercentage(parseFloat(value))}
              autoComplete="off"
              helpText="Enter a percentage value (e.g. 10 for 10% off)"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Maintenance Management Modal */}
      <Modal
        open={maintenanceDialogOpen}
        onClose={() => setMaintenanceDialogOpen(false)}
        title="Schedule Maintenance"
        primaryAction={{
          content: 'Schedule Maintenance',
          onAction: handleScheduleMaintenance,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setMaintenanceDialogOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Maintenance Title"
              value={maintenanceTitle}
              onChange={setMaintenanceTitle}
              autoComplete="off"
            />
            <TextField
              label="Maintenance Message"
              value={maintenanceMessage}
              onChange={setMaintenanceMessage}
              autoComplete="off"
              helpText="Message to display to users during maintenance"
            />
            <TextField
              label="Start Date"
              type="date"
              value={maintenanceStartDate}
              onChange={setMaintenanceStartDate}
            />
            <TextField
              label="Start Time"
              value={maintenanceStartTime}
              onChange={setMaintenanceStartTime}
              autoComplete="off"
              helpText="HH:MM format (24-hour clock)"
            />
            <TextField
              label="End Date"
              type="date"
              value={maintenanceEndDate}
              onChange={setMaintenanceEndDate}
            />
            <TextField
              label="End Time"
              value={maintenanceEndTime}
              onChange={setMaintenanceEndTime}
              autoComplete="off"
              helpText="HH:MM format (24-hour clock)"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Create Referral Partner Modal */}
      <Modal
        open={createReferralDialogOpen}
        onClose={() => setCreateReferralDialogOpen(false)}
        title="Create Referral Partner"
        primaryAction={{
          content: 'Create Partner',
          onAction: handleCreateReferralPartner,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setCreateReferralDialogOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Search for User"
              value={referralSearchValue}
              onChange={setReferralSearchValue}
              placeholder="Search by name or email"
              clearButton
              onClearButtonClick={() => {
                setReferralSearchValue('');
                setSelectedUser(null);
              }}
              helpText="Search for an existing user to link to this referral partner"
            />
            
            {referralSearchValue && (
              <div style={{ marginTop: '0.5rem', maxHeight: '200px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
              <ResourceList
                resourceName={{ singular: 'user', plural: 'users' }}
                items={filterUsersForReferral()?.slice(0, 5) || []}
                renderItem={(user) => {
                  return (
                    <ResourceItem
                      id={user.id}
                      onClick={() => {
                        setSelectedUser(user);
                        setReferralSearchValue(user.email || '');
                      }}
                    >
                      <h3>
                        <TextStyle variation="strong">{user.name}</TextStyle>
                      </h3>
                      <div>{user.email}</div>
                    </ResourceItem>
                  );
                }}
              />
            </div>
            )}
            
            {selectedUser && (
              <div style={{ marginTop: '0.5rem', padding: '0.5rem', backgroundColor: '#f4f6f8', borderRadius: '4px' }}>
                <Text variant="bodyMd">Selected User: <TextStyle variation="strong">{selectedUser.name}</TextStyle> ({selectedUser.email})</Text>
              </div>
            )}
            
            <TextField
              label="Name"
              value={newReferralName}
              onChange={setNewReferralName}
              autoComplete="off"
              disabled={selectedUser !== null}
              helpText={selectedUser ? "Name from selected user" : "Enter partner name"}
            />
            <TextField
              label="Email"
              value={newReferralEmail}
              onChange={setNewReferralEmail}
              autoComplete="off"
              disabled={selectedUser !== null}
              helpText={selectedUser ? "Email from selected user" : "Enter partner email"}
            />
            <TextField
              label="Commission Percentage"
              type="number"
              value={newReferralCommission}
              onChange={(value) => setNewReferralCommission(value)}
              autoComplete="off"
              suffix="%"
              helpText="Percentage commission on referred user subscriptions"
            />
            <Checkbox
              label="Use Manual Referral Code"
              checked={useManualCode}
              onChange={setUseManualCode}
            />
            {useManualCode && (
              <TextField
                label="Manual Referral Code"
                value={manualReferralCode}
                onChange={setManualReferralCode}
                autoComplete="off"
                helpText="Enter a custom referral code for this partner"
              />
            )}
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Edit Referral Partner Modal */}
      <Modal
        open={editReferralDialogOpen}
        onClose={() => setEditReferralDialogOpen(false)}
        title={`Edit Referral Partner ${editReferralPartner?.partnerName || editReferralPartner?.name || ''}`}
        primaryAction={{
          content: 'Save Changes',
          onAction: handleSaveReferralPartner,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setEditReferralDialogOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Name"
              value={newReferralName}
              onChange={setNewReferralName}
              autoComplete="off"
            />
            <TextField
              label="Email"
              value={newReferralEmail}
              onChange={setNewReferralEmail}
              autoComplete="off"
            />
            <TextField
              label="Commission Percentage"
              type="number"
              value={newReferralCommission}
              onChange={(value) => setNewReferralCommission(value)}
              autoComplete="off"
              suffix="%"
              helpText="Percentage commission on referred user subscriptions"
            />
            <TextField
              label="Referral Code"
              value={manualReferralCode}
              onChange={setManualReferralCode}
              autoComplete="off"
              helpText="Edit the referral code for this partner"
            />
            <div style={{ marginTop: '1rem' }}>
              <Text variant="headingMd">Link to User Account</Text>
              <div style={{ marginTop: '0.5rem' }}>
                <TextField
                  label="Search Users"
                  value={referralSearchValue}
                  onChange={setReferralSearchValue}
                  placeholder="Search by name or email"
                  clearButton
                  onClearButtonClick={() => {
                    setReferralSearchValue('');
                    setSelectedUser(null);
                  }}
                />
              </div>
            </div>
            {selectedUser && (
              <Banner status="info" title="Selected User">
                <p><strong>Name:</strong> {selectedUser.fullName || 'No name'}</p>
                <p><strong>Email:</strong> {selectedUser.email}</p>
              </Banner>
            )}
            
            {editReferralPartner && (
              <TextContainer>
                <p>Referral Partner Information</p>
                <p><strong>Partner ID:</strong> {editReferralPartner.id}</p>
                <p><strong>Partner Name:</strong> {editReferralPartner.partnerName || editReferralPartner.name || 'Unknown'}</p>
                <p><strong>Partner Email:</strong> {editReferralPartner.partnerEmail || editReferralPartner.email || 'Unknown'}</p>
                <p><strong>Commission Percentage:</strong> {editReferralPartner.commission || 0}%</p>
                <p><strong>Referral Code:</strong> {editReferralPartner.code}</p>
              </TextContainer>
            )}
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Process/Edit Payout Modal */}
      <Modal
        open={showPayoutModal}
        onClose={() => {
          setShowPayoutModal(false);
          setSelectedPartnerForPayout(null);
          setEditingPayout(null);
          setPayoutAmount('');
          setPayoutTransactionId('');
          setPayoutStatus('completed');
        }}
        title={editingPayout ? `Edit Payout for ${editingPayout.partnerName}` : `Process Payout for ${selectedPartnerForPayout?.partnerName || ''}`}
        primaryAction={{
          content: editingPayout ? 'Update Payout' : 'Process Payout',
          onAction: handleProcessPayout,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowPayoutModal(false);
              setSelectedPartnerForPayout(null);
              setEditingPayout(null);
              setPayoutAmount('');
              setPayoutTransactionId('');
              setPayoutStatus('completed');
            },
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Amount"
              type="number"
              value={payoutAmount}
              onChange={setPayoutAmount}
              prefix="$"
              autoComplete="off"
            />
            <TextField
              label="Transaction ID"
              value={payoutTransactionId}
              onChange={setPayoutTransactionId}
              autoComplete="off"
              helpText="Optional: Enter a transaction ID for tracking purposes"
            />
            <Select
              label="Status"
              options={[
                {label: 'Completed', value: 'completed'},
                {label: 'Pending', value: 'pending'},
                {label: 'Failed', value: 'failed'}
              ]}
              value={payoutStatus}
              onChange={setPayoutStatus}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Referral Bonus Modal */}
      <Modal
        open={showReferralBonusModal}
        onClose={() => setShowReferralBonusModal(false)}
        title="Edit Referral Bonus"
        primaryAction={{
          content: 'Save Changes',
          onAction: updateReferralBonusSettings,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowReferralBonusModal(false),
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Referral Bonus Credits"
              type="number"
              value={referralBonusSettings.toString()}
              onChange={(value) => {
                // Convert to number or set to empty string if invalid
                const numValue = parseInt(value);
                setReferralBonusSettings(isNaN(numValue) ? 0 : numValue);
              }}
              min="0"
              autoComplete="off"
              helpText="Number of flex credits awarded to new users who register with a referral code"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Trial Plan Settings Modal */}
      <Modal
        open={showTrialPlanModal}
        onClose={() => setShowTrialPlanModal(false)}
        title="Edit Trial Plan Settings"
        primaryAction={{
          content: 'Save',
          onAction: updateTrialPlanSettings,
          loading: loadingTrialPlan
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowTrialPlanModal(false)
          }
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Trial Credits"
              type="number"
              value={trialPlanSettings.credits}
              onChange={(value) => setTrialPlanSettings({...trialPlanSettings, credits: value})}
              helpText="Number of credits to provide during the trial period"
            />
            <TextField
              label="Trial Duration (days)"
              type="number"
              value={trialPlanSettings.duration_days}
              onChange={(value) => setTrialPlanSettings({...trialPlanSettings, duration_days: value})}
              helpText="Duration of the trial period in days"
            />
            <TextField
              label="Stripe Product ID"
              value={trialPlanSettings.stripe_product_id}
              onChange={(value) => setTrialPlanSettings({...trialPlanSettings, stripe_product_id: value})}
              helpText="Stripe Product ID for the trial plan"
            />
            <TextField
              label="Stripe Price ID"
              value={trialPlanSettings.stripe_price_id}
              onChange={(value) => setTrialPlanSettings({...trialPlanSettings, stripe_price_id: value})}
              helpText="Stripe Price ID for the trial plan"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  );
}